// src/components/admin/PushCampaignsEditor.jsx - Part 1 of 2
// WITH CUSTOMER PREVIEW MODAL, IMAGE/GIF SUPPORT & ENHANCED TARGETING

import React, { useState, useEffect } from 'react';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  limit
} from 'firebase/firestore';
import { db } from '../../utils/firebase';
import { DEFAULT_PUSH_TEMPLATES, AUTO_PUSH_TEMPLATE_IDS } from '../../utils/transactionalTemplates';
import { useSalonAuth } from '../../contexts/SalonAuthContext';
import { getCustomerTargetingPreview, getSalonServices, getSalonStaff } from '../../api/salonApi';
import { applyIndustryContent, getSalonIndustry } from '../../utils/industryContent';
import { findUnresolvedPlaceholders, personalizeMessage, getSalonPhone, getSalonName, getCustomerAppointment, getCustomerTransaction } from '../../utils/campaignPersonalization';
import '../../styles/customer-preview-modal.css';
import CampaignFrequencyControl from './CampaignFrequencyControl';
import FinalCustomersTargeted from './FinalCustomersTargeted';
import { applyFrequencyExclusionToList, recordCampaignDeliveries } from '../../api/campaignExclusionService';
import { useSalonLocations } from '../../hooks/useSalonLocations';

// Customer Preview Modal Component
function CustomerPreviewModal({ isOpen, onClose, previewData, campaignName }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [filterText, setFilterText] = useState('');
  const [sortField, setSortField] = useState('displayName');
  const [sortDirection, setSortDirection] = useState('asc');
  const customersPerPage = 100;

  useEffect(() => {
    setCurrentPage(1);
  }, [filterText]);

  if (!isOpen || !previewData) return null;

  const { eligibleCustomers = [], eligibleCount = 0, totalCount = 0 } = previewData;

  const filteredCustomers = eligibleCustomers.filter(customer => {
    if (!filterText) return true;
    const searchLower = filterText.toLowerCase();
    return (
      customer.displayName?.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower)
    );
  });

  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    const aVal = a[sortField] || '';
    const bVal = b[sortField] || '';
    
    if (sortDirection === 'asc') {
      return aVal.localeCompare(bVal);
    } else {
      return bVal.localeCompare(aVal);
    }
  });

  const totalPages = Math.ceil(sortedCustomers.length / customersPerPage);
  const startIndex = (currentPage - 1) * customersPerPage;
  const endIndex = startIndex + customersPerPage;
  const currentCustomers = sortedCustomers.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExportCSV = () => {
    const headers = ['Name', 'Email'];
    const csvRows = [headers.join(',')];

    sortedCustomers.forEach(customer => {
      const row = [
        `"${customer.displayName || ''}"`,
        `"${customer.email || ''}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    const date = new Date().toISOString().split('T')[0];
    const filename = `push-campaign-recipients-${campaignName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${date}.csv`;
    
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const coverage = totalCount > 0 ? ((eligibleCount / totalCount) * 100).toFixed(1) : '0.0';

  return (
    <div className="modal-overlay">
      <div className="customer-preview-modal">
        <div className="modal-header">
          <h3>Push Notification Recipients Preview</h3>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-summary">
          <div className="summary-item">
            <span className="summary-label">Eligible Recipients</span>
            <span className="summary-value">{eligibleCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Total Customers</span>
            <span className="summary-value">{totalCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Coverage</span>
            <span className="summary-value coverage">{coverage}%</span>
          </div>
        </div>

        <div className="modal-body">
          {eligibleCount > 0 ? (
            <>
              <button 
                className="export-csv-btn" 
                onClick={handleExportCSV}
                disabled={sortedCustomers.length === 0}
              >
                📥 Export to CSV
              </button>

              <div className="table-filter">
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
                {filterText && (
                  <button 
                    className="filter-clear-btn"
                    onClick={() => setFilterText('')}
                  >
                    Clear
                  </button>
                )}
              </div>

              {currentCustomers.length > 0 ? (
                <>
                  <div className="customer-table-container">
                    <table className="customer-table">
                      <thead>
                        <tr>
                          <th 
                            onClick={() => handleSort('displayName')}
                            className={sortField === 'displayName' ? `sorted-${sortDirection}` : ''}
                          >
                            Customer Name
                            <span className="sort-indicator"></span>
                          </th>
                          <th 
                            onClick={() => handleSort('email')}
                            className={sortField === 'email' ? `sorted-${sortDirection}` : ''}
                          >
                            Email Address
                            <span className="sort-indicator"></span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentCustomers.map((customer, index) => (
                          <tr key={customer.id || index}>
                            <td>{customer.displayName || 'N/A'}</td>
                            <td>{customer.email || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>
                      
                      <span className="pagination-info">
                        Page {currentPage} of {totalPages}
                      </span>
                      
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">🔍</div>
                  <p className="empty-state-text">No customers match your search</p>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">👥</div>
              <p className="empty-state-text">No eligible recipients found for the selected criteria</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PushCampaignsEditor({ salonId, onUpdate, prefillSubject, prefillBody, onPrefillConsumed, prefillCampaign, onCampaignPrefillConsumed }) {
  const { currentUser } = useSalonAuth();
  const { locations } = useSalonLocations(salonId);
  const isMultiLocation = locations.length > 1;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(''); // '', 'test', 'actual', or 'template'
  const [success, setSuccess] = useState('');
  
  const [pushErrors, setPushErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});

  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  
  // NEW: State for tracking if targeting criteria has been saved
  const [targetingCriteriaSaved, setTargetingCriteriaSaved] = useState(false);
  
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [customerSearchResults, setCustomerSearchResults] = useState([]);
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [searchingCustomers, setSearchingCustomers] = useState(false);

  const [imagePreview, setImagePreview] = useState('');
  const [imageError, setImageError] = useState('');
  const [previewDevice, setPreviewDevice] = useState('mobile');

  // Sub-tab state
  const [activeSubTab, setActiveSubTab] = useState('create-campaign');

  // Template state
  const [selectedTemplateCategory, setSelectedTemplateCategory] = useState('transactional');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [customTemplates, setCustomTemplates] = useState({ transactional: [], promotional: [] });
  const [campaignSettings, setCampaignSettings] = useState({});
  const [templateEditMode, setTemplateEditMode] = useState(false);
  const [templateToEdit, setTemplateToEdit] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // State for appointment-based targeting
  const [salonServices, setSalonServices] = useState([]);
  const [salonStaff, setSalonStaff] = useState([]);
  const [salonPhone, setSalonPhone] = useState('');
  const [industry, setIndustry] = useState('hair');
  useEffect(() => {
    if (!salonId) return;
    getSalonIndustry(salonId).then(setIndustry);
  }, [salonId]);
  const [loadingServicesStaff, setLoadingServicesStaff] = useState(false);
  const [businessName, setBusinessName] = useState('');

  // UPDATED: Default values with "not_selected" options
  const [formData, setFormData] = useState({
    subject: '',
    body: '',
    imageUrl: '',
    schedulingType: 'one-time', // one-time or recurring
    scheduledDate: '',
    scheduledTime: '',
    conversionPeriod: 30,
    recurring: {
      frequency: 'daily', // daily, weekly, monthly, annually
      dayOfWeek: '', // for weekly
      dayOfMonth: 0, // for monthly (1-31)
      monthOfYear: '', // for annually
      dayOfYear: 0, // for annually (1-31)
      startDate: '',
      endDate: '',
      endType: 'date', // date or cycles
      numberOfCycles: 0,
      excludePreviousRecipients: false,
      exclusionType: 'cycles', // cycles or date
      excludePastCycles: 0,
      excludeBeforeDate: ''
    },
    targetingType: 'not_selected',
    targetingCriteria: {
      birthMonths: [],
      postalCodes: [],
      lastTransactionMonths: 0,
      lastTransactionUnit: 'months',
      transactionCount: 0,
      transactionType: 'not_selected',
      transactionAmountType: 'not_selected',
      transactionAmount: 0,
      transactionLocationId: 'not_selected',
      memberTiers: [],
      pointsType: 'not_selected',
      pointsAmount: 0,
      specificCustomers: [],
      notesSearchText: '', // #Revised UI_v2 — customer notes targeting
      notesMatchType: 'contains_all', // #Revised UI_v2 — customer notes targeting
      appointmentTimeValue: 0,
      appointmentTimeUnit: 'days',
      appointmentService: 'not_selected',
      appointmentStaff: 'not_selected',
      appointmentStatus: []
    }
  });

  // Appointment status options
  const appointmentStatusOptions = ['approved', 'pending', 'rejected', 'cancelled'];

  const birthMonthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const memberTierOptions = [
    'Bronze', 'Silver', 'Gold', 'Platinum', 'Diamond'
  ];

  const commonEmojis = ['😊', '🎉', '💄', '✨', '💅', '💗', '🌟', '💖', '🔥', '💯', '👑', '💃'];

  // Push Notification Campaign Templates
  const pushTemplates = {
    transactional: [
      {
        id: 'appointment_reminder',
        title: 'Appointment Tomorrow',
        messageCategory: 'transactional',
        messageType: 'appointment_update',
        subject: DEFAULT_PUSH_TEMPLATES.appointment_reminder.title,
        body: DEFAULT_PUSH_TEMPLATES.appointment_reminder.body,
        targetingType: 'specific_customer',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      },
      {
        id: 'payment_confirmation',
        title: 'Payment Received',
        messageCategory: 'transactional',
        messageType: 'payment_update',
        subject: DEFAULT_PUSH_TEMPLATES.payment_confirmation.title,
        body: DEFAULT_PUSH_TEMPLATES.payment_confirmation.body,
        targetingType: 'specific_customer',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      },
      {
        id: 'loyalty_update',
        title: 'Points Earned!',
        messageCategory: 'transactional',
        messageType: 'loyalty_update',
        subject: DEFAULT_PUSH_TEMPLATES.loyalty_update.title,
        body: DEFAULT_PUSH_TEMPLATES.loyalty_update.body,
        targetingType: 'specific_customer',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      },
      {
        id: 'appointment_approved',
        title: 'Appointment Approved',
        messageCategory: 'transactional',
        messageType: 'appointment_update',
        subject: DEFAULT_PUSH_TEMPLATES.appointment_approved.title,
        body: DEFAULT_PUSH_TEMPLATES.appointment_approved.body,
        targetingType: 'specific_customer',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      },
      {
        id: 'appointment_rejected',
        title: 'Appointment Rejected',
        messageCategory: 'transactional',
        messageType: 'appointment_update',
        subject: DEFAULT_PUSH_TEMPLATES.appointment_rejected.title,
        body: DEFAULT_PUSH_TEMPLATES.appointment_rejected.body,
        targetingType: 'specific_customer',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      },
      {
        id: 'wallet_debit',
        title: 'Wallet Debited',
        messageCategory: 'transactional',
        messageType: 'payment_update',
        subject: DEFAULT_PUSH_TEMPLATES.wallet_debit.title,
        body: DEFAULT_PUSH_TEMPLATES.wallet_debit.body,
        targetingType: 'specific_customer',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '',
          notesMatchType: 'contains_all'
        }
      },
      {
        id: 'wallet_topup',
        title: 'Wallet Top-Up Verified',
        messageCategory: 'transactional',
        messageType: 'payment_update',
        subject: DEFAULT_PUSH_TEMPLATES.wallet_topup.title,
        body: DEFAULT_PUSH_TEMPLATES.wallet_topup.body,
        targetingType: 'specific_customer',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '',
          notesMatchType: 'contains_all'
        }
      },
      {
        id: 'wallet_topup_rejected',
        title: 'Wallet Top-Up Rejected',
        messageCategory: 'transactional',
        messageType: 'payment_update',
        subject: DEFAULT_PUSH_TEMPLATES.wallet_topup_rejected.title,
        body: DEFAULT_PUSH_TEMPLATES.wallet_topup_rejected.body,
        targetingType: 'specific_customer',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '',
          notesMatchType: 'contains_all'
        }
      }
    ],
    promotional: [
      {
        id: 'flash_sale',
        title: 'Flash Sale: 30% Off Today Only!',
        messageCategory: 'promotional',
        messageType: 'not_selected',
        subject: 'Flash Sale: 30% Off Today Only! ⚡',
        body: 'Limited time! Book now and save 30% on all services',
        targetingType: 'customer_points',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 6,
          lastTransactionUnit: 'months',
          transactionCount: 2,
          transactionType: 'greater_than',
          transactionAmountType: 'greater_than',
          transactionAmount: 100,
          memberTiers: ['Gold', 'Platinum'],
          pointsType: 'greater_than',
          pointsAmount: 500,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      },
      {
        id: 'birthday_greeting',
        title: 'Happy Birthday!',
        messageCategory: 'promotional',
        messageType: 'not_selected',
        subject: 'Happy Birthday! 🎉',
        body: 'Celebrate with 25% off any service this month!',
        targetingType: 'customer_profile',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      },
      {
        id: 'new_service',
        title: 'New Treatment Available!',
        messageCategory: 'promotional',
        messageType: 'not_selected',
        subject: 'New Treatment Available! ✨',
        body: 'Try our latest {service_name} - first session 20% off!',
        targetingType: 'all_customers',
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all' // #Revised UI_v2 — customer notes targeting
        }
      }
    ]
  };

  // Load custom templates (localStorage cache) and campaign settings + built-in transactional template
  // overrides (Firestore, the source of truth read by every sender) for this salon
  useEffect(() => {
    let localTemplates = { transactional: [], promotional: [] };
    const savedTemplates = localStorage.getItem(`push_templates_${salonId}`);
    if (savedTemplates) {
      try {
        localTemplates = JSON.parse(savedTemplates);
        setCustomTemplates(localTemplates);
      } catch (err) {
        console.error('Error loading templates:', err);
      }
    }

    const loadFromFirestore = async () => {
      if (!salonId) return;
      try {
        const { doc, getDoc, updateDoc } = await import('firebase/firestore');
        const salonDoc = await getDoc(doc(db, 'salons', salonId));
        if (!salonDoc.exists()) return;
        const salonData = salonDoc.data();
        if (salonData.transactionalCampaignSettings) setCampaignSettings(salonData.transactionalCampaignSettings);

        const stored = salonData.pushTemplates || {};
        const merged = { ...localTemplates, transactional: [...(localTemplates.transactional || [])] };
        for (const templateId of AUTO_PUSH_TEMPLATE_IDS) {
          const defaultTemplate = pushTemplates.transactional.find(t => t.id === templateId);
          if (!defaultTemplate) continue;
          const localIdx = merged.transactional.findIndex(t => t.id === templateId);
          if (stored[templateId]) {
            const override = { ...defaultTemplate, subject: stored[templateId].title, body: stored[templateId].body, isCustom: true };
            if (localIdx !== -1) merged.transactional[localIdx] = override;
            else merged.transactional.push(override);
          } else if (localIdx !== -1) {
            // One-time migration: an edit saved only in this browser is pushed to Firestore so sends use it
            const t = merged.transactional[localIdx];
            try {
              await updateDoc(doc(db, 'salons', salonId), {
                [`pushTemplates.${templateId}`]: { title: t.subject, body: t.body }
              });
            } catch (migrateErr) {
              console.error('Could not migrate local push template to Firestore:', templateId, migrateErr);
            }
          }
        }
        setCustomTemplates(merged);
      } catch (err) {
        console.error('Error loading campaign settings / templates:', err);
      }
    };
    loadFromFirestore();
  }, [salonId]);

  // Load services, staff, and salon phone for appointment-based targeting and personalization
  useEffect(() => {
    const loadServicesStaffAndPhone = async () => {
      if (!salonId) return;

      setLoadingServicesStaff(true);
      try {
        const [services, staff, phone, clientDoc] = await Promise.all([
          getSalonServices(salonId),
          getSalonStaff(salonId),
          getSalonPhone(salonId),
          getDoc(doc(db, 'clients', salonId))
        ]);

        setSalonServices(services.filter(s => s.isActive !== false));
        setSalonStaff(staff.filter(s => s.isActive !== false));
        setSalonPhone(phone || '');
        if (clientDoc.exists()) {
          setBusinessName(clientDoc.data().name || '');
        }
      } catch (err) {
        console.error('Error loading services, staff, and phone:', err);
      } finally {
        setLoadingServicesStaff(false);
      }
    };

    loadServicesStaffAndPhone();
  }, [salonId]);

  // Apply AI-generated prefill when provided
  useEffect(() => {
    if (!prefillSubject && !prefillBody) return;
    setFormData(prev => ({
      ...prev,
      subject: prefillSubject || prev.subject,
      body: prefillBody || prev.body
    }));
    setActiveSubTab('create-campaign');
    window.scrollTo(0, 0);
    if (onPrefillConsumed) onPrefillConsumed();
  }, [prefillSubject, prefillBody]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore a failed campaign for editing — user must choose a new send time.
  // Also handles pre-population from Find Prospects (specific_customer targeting).
  useEffect(() => {
    if (!prefillCampaign) return;
    const prefillSpecificCustomers = prefillCampaign.targetingCriteria?.specificCustomers || [];
    const hasSpecificCustomers =
      prefillCampaign.targetingType === 'specific_customer' && prefillSpecificCustomers.length > 0;
    setFormData(prev => ({
      ...prev,
      subject: prefillCampaign.subject || '',
      body: prefillCampaign.body || '',
      imageUrl: prefillCampaign.imageUrl || '',
      scheduledDate: '',
      scheduledTime: '',
      schedulingType: prefillCampaign.schedulingType || 'one-time',
      recurring: prefillCampaign.recurring
        ? { ...prev.recurring, ...prefillCampaign.recurring }
        : prev.recurring,
      targetingType: prefillCampaign.targetingType || 'not_selected',
      // Merge with defaults so every field has a defined value regardless of campaign age
      targetingCriteria: {
        ...prev.targetingCriteria,
        ...(prefillCampaign.targetingCriteria || {}),
      },
    }));
    // When coming from Find Prospects, pre-populate the customer chips and mark as saved
    if (hasSpecificCustomers) {
      setSelectedCustomers(prefillSpecificCustomers);
      setTargetingCriteriaSaved(true);
    } else {
      setSelectedCustomers([]);
      setTargetingCriteriaSaved(false);
    }
    setActiveSubTab('create-campaign');
    window.scrollTo(0, 0);
    if (onCampaignPrefillConsumed) onCampaignPrefillConsumed();
  }, [prefillCampaign]); // eslint-disable-line react-hooks/exhaustive-deps

  // Save custom templates to localStorage
  const saveTemplatesToStorage = (templates) => {
    localStorage.setItem(`push_templates_${salonId}`, JSON.stringify(templates));
    setCustomTemplates(templates);
  };

  // Get all templates (default + custom); custom entries override defaults with the same id
  const getAllTemplates = (category) => {
    const custom = customTemplates[category] || [];
    const customIds = new Set(custom.map(t => t.id));
    const defaults = applyIndustryContent('push', pushTemplates[category], industry).filter(t => !customIds.has(t.id));
    return [...defaults, ...custom];
  };

  // Function to load template into form
  const handleLoadTemplate = (template) => {
    setFormData({
      subject: template.subject,
      body: template.body,
      imageUrl: '',
      schedulingType: 'one-time',
      scheduledDate: '',
      scheduledTime: '',
      conversionPeriod: template.conversionPeriod ?? 30,
      recurring: {
        frequency: 'daily',
        dayOfWeek: '',
        dayOfMonth: 0,
        monthOfYear: '',
        dayOfYear: 0,
        startDate: '',
        endDate: '',
        endType: 'date',
        numberOfCycles: 0,
        excludePreviousRecipients: false,
        exclusionType: 'cycles',
        excludePastCycles: 0,
        excludeBeforeDate: ''
      },
      targetingType: template.targetingType,
      targetingCriteria: template.targetingCriteria
    });
    setSelectedCustomers([]);
    setTargetingCriteriaSaved(false);
    setSelectedTemplate(null);
    setTemplateEditMode(false);
    setTemplateToEdit(null);
    setImagePreview('');
    setImageError('');
    setFieldErrors({});
    setSuccess('Template loaded successfully! Please fill in the scheduling details and customize as needed.');
    setTimeout(() => setSuccess(''), 5000);

    // Scroll to the form
    const formElement = document.querySelector('.notification-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Toggle transactional campaign enabled/disabled
  const handleToggleCampaign = async (template) => {
    const key = `push_${template.id}`;
    const currentlyEnabled = campaignSettings[key] !== false;
    const newSettings = { ...campaignSettings, [key]: !currentlyEnabled };
    setCampaignSettings(newSettings);

    try {
      const { doc, updateDoc } = await import('firebase/firestore');
      await updateDoc(doc(db, 'salons', salonId), {
        [`transactionalCampaignSettings.${key}`]: !currentlyEnabled
      });
      console.log(`Push campaign ${template.id} ${!currentlyEnabled ? 'enabled' : 'disabled'}`);
    } catch (err) {
      console.error('Error toggling campaign:', err);
      setCampaignSettings(campaignSettings);
    }
  };

  // Function to revise template
  const handleReviseTemplate = (template) => {
    setFormData({
      subject: template.subject,
      body: template.body,
      imageUrl: '',
      schedulingType: 'one-time',
      scheduledDate: '',
      scheduledTime: '',
      conversionPeriod: template.conversionPeriod ?? 30,
      recurring: {
        frequency: 'daily',
        dayOfWeek: '',
        dayOfMonth: 0,
        monthOfYear: '',
        dayOfYear: 0,
        startDate: '',
        endDate: '',
        endType: 'date',
        numberOfCycles: 0,
        excludePreviousRecipients: false,
        exclusionType: 'cycles',
        excludePastCycles: 0,
        excludeBeforeDate: ''
      },
      targetingType: template.targetingType,
      targetingCriteria: template.targetingCriteria
    });
    setSelectedCustomers([]);
    setTargetingCriteriaSaved(false);
    setTemplateEditMode(true);
    setTemplateToEdit(template);
    setSelectedTemplate(null);
    setImagePreview('');
    setImageError('');
    setFieldErrors({});
    setSuccess('Template loaded for editing. Make your changes and click "Make Revisions to Template" to save.');
    setTimeout(() => setSuccess(''), 5000);

    // Scroll to the form
    const formElement = document.querySelector('.notification-form');
    if (formElement) {
      formElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Function to save revised template
  const handleSaveRevisedTemplate = async () => {
    if (!templateToEdit) return;

    // Validation
    if (!formData.subject.trim() || !formData.body.trim()) {
      setError('Please fill in the title and message body');
      setTimeout(() => setError(''), 5000);
      return;
    }

    const category = templateToEdit.messageCategory;
    const updatedCustomTemplates = { ...customTemplates, [category]: [...(customTemplates[category] || [])] };

    const revised = {
      ...templateToEdit,
      title: formData.subject,
      subject: formData.subject,
      body: formData.body,
      targetingType: formData.targetingType,
      targetingCriteria: formData.targetingCriteria,
      isCustom: true
    };

    const templateIndex = updatedCustomTemplates[category].findIndex(t => t.id === templateToEdit.id);
    if (templateIndex !== -1) {
      updatedCustomTemplates[category][templateIndex] = revised;
    } else {
      // Default template being revised — save as custom override with same id
      updatedCustomTemplates[category].push(revised);
    }

    saveTemplatesToStorage(updatedCustomTemplates);

    // Built-in auto-triggered templates are saved to Firestore so every sender (any admin's browser, the Bookings
    // AI agent, the reminder Cloud Function) uses the same wording. localStorage above is only a local cache.
    if (AUTO_PUSH_TEMPLATE_IDS.includes(templateToEdit.id) && salonId) {
      try {
        const { doc, updateDoc } = await import('firebase/firestore');
        await updateDoc(doc(db, 'salons', salonId), {
          [`pushTemplates.${templateToEdit.id}`]: { title: formData.subject, body: formData.body }
        });
      } catch (firestoreError) {
        console.error('Error saving push template to Firestore:', firestoreError);
        setError('Template saved on this device only - could not save it to the salon. Please try again.');
        setTimeout(() => setError(''), 7000);
        return;
      }
    }

    setTemplateEditMode(false);
    setTemplateToEdit(null);
    resetPushForm();
    setSuccess('Template revised successfully!');
    setTimeout(() => setSuccess(''), 5000);
  };

  // Function to create new template
  const handleCreateTemplate = () => {
    // Validation
    if (!formData.subject.trim() || !formData.body.trim()) {
      setError('Please fill in the title and message body');
      setTimeout(() => setError(''), 5000);
      return;
    }

    // Determine category based on targeting type
    const category = formData.targetingType === 'specific_customer' ? 'transactional' : 'promotional';

    const newTemplate = {
      id: `custom_${Date.now()}`,
      title: formData.subject,
      messageCategory: category,
      messageType: category === 'transactional' ? 'appointment_update' : 'not_selected',
      subject: formData.subject,
      body: formData.body,
      targetingType: formData.targetingType,
      targetingCriteria: formData.targetingCriteria,
      isCustom: true
    };

    const updatedCustomTemplates = { ...customTemplates };
    updatedCustomTemplates[category] = [...(updatedCustomTemplates[category] || []), newTemplate];

    saveTemplatesToStorage(updatedCustomTemplates);
    resetPushForm();
    setSuccess('Template created successfully!');
    setTimeout(() => setSuccess(''), 5000);

    // Scroll to templates section
    const templatesElement = document.querySelector('.templates-section');
    if (templatesElement) {
      templatesElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  // Function to delete template
  const handleDeleteTemplate = () => {
    if (!selectedTemplate || !selectedTemplate.isCustom) {
      setError('You can only delete custom templates');
      setTimeout(() => setError(''), 5000);
      return;
    }

    setShowDeleteConfirm(true);
  };

  const confirmDeleteTemplate = () => {
    const category = selectedTemplate.messageCategory;
    const updatedCustomTemplates = { ...customTemplates };

    updatedCustomTemplates[category] = updatedCustomTemplates[category].filter(
      t => t.id !== selectedTemplate.id
    );

    saveTemplatesToStorage(updatedCustomTemplates);
    setSelectedTemplate(null);
    setShowDeleteConfirm(false);
    setSuccess('Template deleted successfully!');
    setTimeout(() => setSuccess(''), 5000);
  };

  const handlePreviewTargeting = async () => {
    try {
      setPreviewLoading(true);
      setError('');
      const preview = await getCustomerTargetingPreview(salonId, {
        targetingType: formData.targetingType,
        targetingCriteria: formData.targetingCriteria
      });
      setPreviewData(preview);
      setShowPreviewModal(true);
    } catch (err) {
      console.error('Error getting preview:', err);
      setError('Failed to get targeting preview. Please try again.');
      setTimeout(() => setError(''), 5000);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleDownloadSocialCSV = async () => {
    try {
      const preview = await getCustomerTargetingPreview(salonId, {
        targetingType: formData.targetingType,
        targetingCriteria: formData.targetingCriteria
      });
      const customers = preview.eligibleCustomers || [];
      const rows = [['first_name', 'last_name', 'email', 'phone']];
      customers.forEach(c => {
        const nameParts = (c.displayName || '').trim().split(/\s+/);
        const firstName = nameParts[0] || '';
        const lastName = nameParts.slice(1).join(' ') || '';
        const email = (c.email || '').toLowerCase();
        const digitsOnly = (c.phone || '').replace(/\D/g, '');
        const phone = digitsOnly.length === 8 ? `65${digitsOnly}` : digitsOnly;
        rows.push([firstName, lastName, email, phone]);
      });
      const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'social_platform_audience.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading social CSV:', err);
      setError('Failed to generate CSV. Please try again.');
      setTimeout(() => setError(''), 5000);
    }
  };

  const validateImageUrl = (url) => {
    if (!url) {
      setImageError('');
      setImagePreview('');
      return true;
    }

    try {
      new URL(url);
    } catch (e) {
      setImageError('Please enter a valid URL');
      setImagePreview('');
      return false;
    }

    const validExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.svg'];
    const urlLower = url.toLowerCase();
    const hasValidExtension = validExtensions.some(ext => urlLower.includes(ext));
    
    const commonImageHosts = ['imgur.com', 'giphy.com', 'tenor.com', 'cloudinary.com', 'imagekit.io'];
    const hasImageHost = commonImageHosts.some(host => urlLower.includes(host));

    if (!hasValidExtension && !hasImageHost) {
      setImageError('URL should point to an image (jpg, png, gif, etc.)');
      setImagePreview('');
      return false;
    }

    setImageError('');
    setImagePreview(url);
    return true;
  };

  const handleImageUrlChange = (value) => {
    handleInputChange('imageUrl', value);
    
    const timeoutId = setTimeout(() => {
      validateImageUrl(value);
    }, 500);
    
    return () => clearTimeout(timeoutId);
  };

  const handleImageLoadError = () => {
    setImageError('Failed to load image. Please check the URL.');
    setImagePreview('');
  };

  const clearImage = () => {
    handleInputChange('imageUrl', '');
    setImagePreview('');
    setImageError('');
  };

  const searchCustomers = async (searchTerm) => {
    if (!searchTerm || searchTerm.length < 2) {
      setCustomerSearchResults([]);
      return;
    }

    setSearchingCustomers(true);
    try {
      const customersRef = collection(db, 'customers');
      const searchLower = searchTerm.toLowerCase();

      const emailQuery = query(
        customersRef,
        where('salonId', '==', salonId),
        where('email', '>=', searchLower),
        where('email', '<=', searchLower + ''),
        limit(20)
      );

      const nameQuery = query(
        customersRef,
        where('salonId', '==', salonId),
        where('displayName', '>=', searchTerm),
        where('displayName', '<=', searchTerm + ''),
        limit(20)
      );

      const [emailSnapshot, nameSnapshot] = await Promise.all([
        getDocs(emailQuery),
        getDocs(nameQuery)
      ]);

      const customerMap = new Map();

      const addFromSnapshot = (snapshot) => {
        snapshot.forEach((doc) => {
          if (!customerMap.has(doc.id)) {
            const customerData = doc.data();
            customerMap.set(doc.id, {
              id: doc.id,
              email: customerData.email || null,
              displayName: customerData.displayName || null,
              phoneNumber: customerData.phoneNumber || null,
              fcmToken: customerData.fcmToken || null,
              uid: customerData.uid || null
            });
          }
        });
      };

      addFromSnapshot(emailSnapshot);
      addFromSnapshot(nameSnapshot);

      const customers = Array.from(customerMap.values());

      const uniqueCustomers = customers.filter((customer, index, self) =>
        index === self.findIndex(c => c.id === customer.id) &&
        !selectedCustomers.some(selected => selected.id === customer.id)
      );

      setCustomerSearchResults(uniqueCustomers);
    } catch (err) {
      console.error('Error searching customers:', err);
      setCustomerSearchResults([]);
    } finally {
      setSearchingCustomers(false);
    }
  };

  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearchQuery(value);
    setShowCustomerDropdown(true);
    
    const timeoutId = setTimeout(() => {
      searchCustomers(value);
    }, 300);
    
    return () => clearTimeout(timeoutId);
  };

  const handleCustomerSelect = (customer) => {
    const updatedCustomers = [...selectedCustomers, customer];
    setSelectedCustomers(updatedCustomers);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    setShowCustomerDropdown(false);
    
    handleTargetingCriteriaChange('specificCustomers', updatedCustomers);
  };

  const handleCustomerRemove = (customerId) => {
    const updatedCustomers = selectedCustomers.filter(customer => customer.id !== customerId);
    setSelectedCustomers(updatedCustomers);
    handleTargetingCriteriaChange('specificCustomers', updatedCustomers);
  };

  const validateField = (fieldName, value) => {
    let isValid = true;
    let errorMessage = '';

    switch (fieldName) {
      case 'subject':
        if (!value || !value.trim()) {
          errorMessage = 'Title is required';
          isValid = false;
        }
        break;
      case 'body':
        if (!value || !value.trim()) {
          errorMessage = 'Message content is required';
          isValid = false;
        }
        break;
      case 'scheduledDate':
        if (formData.schedulingType === 'one-time' && !value) {
          errorMessage = 'Scheduled date is required';
          isValid = false;
        }
        break;
      case 'scheduledTime':
        if (!value) {
          errorMessage = 'Scheduled time is required';
          isValid = false;
        }
        break;
      case 'targetingType':
        if (value === 'not_selected') {
          errorMessage = 'Target audience is required';
          isValid = false;
        }
        break;
      default:
        break;
    }

    setFieldErrors(prev => ({
      ...prev,
      [fieldName]: errorMessage
    }));

    return isValid;
  };

  const handleFieldBlur = (e) => {
    const { name, value } = e.target;
    validateField(name, value);
  };

  const validatePushCampaign = () => {
    const errors = {};

    if (formData.messageCategory === 'promotional') {
      const leftovers = [...findUnresolvedPlaceholders(formData.subject, { promotional: true }), ...findUnresolvedPlaceholders(formData.body, { promotional: true })];
      if (leftovers.length) errors.body = `Replace these placeholders before sending: ${[...new Set(leftovers)].join(', ')}`;
    }

    if (!formData.subject.trim()) {
      errors.subject = 'Title is required';
    }

    if (!formData.body.trim()) {
      errors.body = 'Message content is required';
    }

    if (!formData.scheduledTime) {
      errors.scheduledTime = 'Scheduled time is required';
    }

    // One-time campaign validation
    if (formData.schedulingType === 'one-time') {
      if (!formData.scheduledDate) {
        errors.scheduledDate = 'Scheduled date is required';
      }

      if (formData.scheduledDate && formData.scheduledTime) {
        const scheduledDateTime = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
        const now = new Date();
        if (scheduledDateTime <= now) {
          errors.scheduledDateTime = 'Scheduled date and time must be in the future';
        }
      }
    }

    // Recurring campaign validation
    if (formData.schedulingType === 'recurring') {
      if (!formData.recurring.startDate) {
        errors.recurringStartDate = 'Start date is required for recurring campaigns';
      }

      if (formData.recurring.endType === 'date' && !formData.recurring.endDate) {
        errors.recurringEndDate = 'End date is required';
      }

      if (formData.recurring.endType === 'date' && formData.recurring.startDate && formData.recurring.endDate) {
        if (new Date(formData.recurring.endDate) <= new Date(formData.recurring.startDate)) {
          errors.recurringEndDate = 'End date must be after start date';
        }
      }

      if (formData.recurring.endType === 'cycles' && formData.recurring.numberOfCycles < 1) {
        errors.numberOfCycles = 'Number of cycles must be at least 1';
      }

      if (formData.recurring.startDate) {
        const startDate = new Date(formData.recurring.startDate);
        const now = new Date();
        now.setHours(0, 0, 0, 0);
        if (startDate < now) {
          errors.recurringStartDate = 'Start date must be today or in the future';
        }
      }

      // Exclusion settings validation
      if (formData.recurring.excludePreviousRecipients) {
        if (formData.recurring.exclusionType === 'cycles' && formData.recurring.excludePastCycles < 1) {
          errors.excludePastCycles = 'Number of past cycles must be at least 1';
        }

        if (formData.recurring.exclusionType === 'date' && !formData.recurring.excludeBeforeDate) {
          errors.excludeBeforeDate = 'Exclusion cutoff date is required';
        }

        if (formData.recurring.exclusionType === 'date' && formData.recurring.excludeBeforeDate && formData.recurring.startDate) {
          if (new Date(formData.recurring.excludeBeforeDate) >= new Date(formData.recurring.startDate)) {
            errors.excludeBeforeDate = 'Exclusion cutoff date must be before campaign start date';
          }
        }
      }
    }

    if (formData.targetingType === 'specific_customer' && formData.targetingCriteria.specificCustomers.length === 0) {
      errors.specificCustomers = 'Please select at least one customer for specific customer targeting';
    }

    if (formData.imageUrl && !validateImageUrl(formData.imageUrl)) {
      errors.imageUrl = imageError || 'Invalid image URL';
    }

    setPushErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const getAdminUsers = async () => {
    try {
      const adminQuery = query(
        collection(db, 'salonUsers'),
        where('salonId', '==', salonId)
      );
      
      const adminSnapshot = await getDocs(adminQuery);
      const adminUsers = [];
      
      adminSnapshot.forEach((doc) => {
        const userData = doc.data();
        if (['owner', 'admin', 'manager'].includes(userData.role)) {
          adminUsers.push(userData);
        }
      });
      
      console.log(`Found ${adminUsers.length} admin users for salon ${salonId}:`, adminUsers);
      return adminUsers;
    } catch (error) {
      console.error('Error fetching admin users:', error);
      throw error;
    }
  };

  const showValidationErrors = (errors) => {
    const errorMessages = Object.values(errors).filter(msg => msg);
    if (errorMessages.length > 0) {
      alert(`Please fix the following errors:\n\n• ${errorMessages.join('\n• ')}`);
    }
  };

  // NEW: Handler for saving targeting criteria
  const handleSaveTargetingCriteria = () => {
    setTargetingCriteriaSaved(true);
    setSuccess('Targeting criteria saved successfully!');
    setTimeout(() => setSuccess(''), 3000);
  };
// src/components/admin/PushCampaignsEditor.jsx - Part 2 of 2
// Event Handlers and JSX Render
// This continues immediately after Part 1

  const getTargetedCustomers = async () => {
    try {
      console.log('Getting targeted customers for salonId:', salonId);
      
      const customersRef = collection(db, 'customers');
      const customersQuery = query(
        customersRef,
        where('salonId', '==', salonId)
      );
      
      const customersSnapshot = await getDocs(customersQuery);
      let customers = [];
      
      customersSnapshot.forEach((doc) => {
        const customerData = doc.data();
        customers.push({
          id: doc.id,
          ...customerData
        });
      });
      
      console.log(`Found ${customers.length} total customers for salon ${salonId}`);

      // For specific_customer targeting, scope diagnostic counts to just the selected customers
      const allCustomers = customers;
      let diagnosticPool = customers;
      if (formData.targetingType === 'specific_customer') {
        const selectedIds = new Set(formData.targetingCriteria.specificCustomers.map(c => c.id));
        diagnosticPool = customers.filter(c => selectedIds.has(c.id));
      }

      // Split into two diagnostic buckets before combining
      const pushOptedIn = diagnosticPool.filter(
        customer => customer.preferences?.notifications?.push !== false
      );
      const withToken = pushOptedIn.filter(customer => customer.fcmToken);

      console.log(`${pushOptedIn.length} customers have push notifications enabled`);
      console.log(`${withToken.length} of those have a valid FCM token`);
      if (pushOptedIn.length > 0 && withToken.length === 0) {
        console.warn(
          'Some customers have push enabled but no FCM token. ' +
          'They need to log into the customer portal and grant browser notification permission.'
        );
      }

      customers = withToken;

      console.log(`${customers.length} customers have push notifications enabled with valid FCM tokens`);
      
      let targetedCustomers = customers;
      
      if (formData.targetingType === 'customer_profile') {
        targetedCustomers = customers.filter(customer => {
          if (formData.targetingCriteria.birthMonths.length > 0) {
            if (!formData.targetingCriteria.birthMonths.includes(customer.birthMonth)) {
              return false;
            }
          }
          
          if (formData.targetingCriteria.postalCodes.length > 0) {
            const customerPostal = customer.postalCode || '';
            const matchesPostal = formData.targetingCriteria.postalCodes.some(code => 
              customerPostal.startsWith(code)
            );
            if (!matchesPostal) {
              return false;
            }
          }
          
          return true;
        });
      } 
      else if (formData.targetingType === 'customer_transaction') {
        const txLocId = formData.targetingCriteria.transactionLocationId;
        targetedCustomers = customers.filter(customer => {
          // Location filter: if a specific location is chosen, match by customer's locationId
          if (txLocId && txLocId !== 'not_selected') {
            if (customer.locationId !== txLocId) return false;
          }

          const totalVisits = customer.metrics?.totalVisits || 0;
          const totalSpent = customer.metrics?.totalSpent || 0;
          const lastTransaction = customer.metrics?.lastTransaction;

          const transactionCount = formData.targetingCriteria.transactionCount;
          const transactionType = formData.targetingCriteria.transactionType;

          if (transactionType === 'less_than' && totalVisits >= transactionCount) {
            return false;
          }
          if (transactionType === 'equal_to' && totalVisits !== transactionCount) {
            return false;
          }
          if (transactionType === 'greater_than' && totalVisits <= transactionCount) {
            return false;
          }

          const transactionAmount = formData.targetingCriteria.transactionAmount;
          const transactionAmountType = formData.targetingCriteria.transactionAmountType;

          if (transactionAmountType === 'greater_than' && totalSpent <= transactionAmount) {
            return false;
          }
          if (transactionAmountType === 'less_than' && totalSpent >= transactionAmount) {
            return false;
          }

          if (lastTransaction) {
            const lastTransactionDate = lastTransaction.toDate ? lastTransaction.toDate() : new Date(lastTransaction);
            const timeValue = formData.targetingCriteria.lastTransactionMonths;
            const timeUnit = formData.targetingCriteria.lastTransactionUnit || 'months';
            const cutoffDate = new Date();

            switch (timeUnit) {
              case 'hours':
                cutoffDate.setHours(cutoffDate.getHours() - timeValue);
                break;
              case 'days':
                cutoffDate.setDate(cutoffDate.getDate() - timeValue);
                break;
              case 'months':
                cutoffDate.setMonth(cutoffDate.getMonth() - timeValue);
                break;
              case 'years':
                cutoffDate.setFullYear(cutoffDate.getFullYear() - timeValue);
                break;
              default:
                cutoffDate.setMonth(cutoffDate.getMonth() - timeValue);
            }

            if (lastTransactionDate < cutoffDate) {
              return false;
            }
          }

          return true;
        });
      }
      else if (formData.targetingType === 'customer_points') {
        targetedCustomers = customers.filter(customer => {
          if (formData.targetingCriteria.memberTiers.length > 0) {
            if (!formData.targetingCriteria.memberTiers.includes(customer.loyaltyTier)) {
              return false;
            }
          }
          
          const pointsBalance = customer.metrics?.pointsBalance || 0;
          const pointsAmount = formData.targetingCriteria.pointsAmount;
          const pointsType = formData.targetingCriteria.pointsType;
          
          if (pointsType === 'greater_than' && pointsBalance <= pointsAmount) {
            return false;
          }
          if (pointsType === 'less_than' && pointsBalance >= pointsAmount) {
            return false;
          }
          
          return true;
        });
      }
      else if (formData.targetingType === 'customer_appointment') {
        // Query appointments collection based on criteria
        const appointmentsRef = collection(db, 'appointments');
        const appointmentsSnapshot = await getDocs(appointmentsRef);

        // Calculate the cutoff date for upcoming appointments
        const now = new Date();
        const cutoffDate = new Date();
        const timeValue = formData.targetingCriteria.appointmentTimeValue;
        const timeUnit = formData.targetingCriteria.appointmentTimeUnit;

        switch (timeUnit) {
          case 'hours':
            cutoffDate.setHours(cutoffDate.getHours() + timeValue);
            break;
          case 'days':
            cutoffDate.setDate(cutoffDate.getDate() + timeValue);
            break;
          case 'months':
            cutoffDate.setMonth(cutoffDate.getMonth() + timeValue);
            break;
          default:
            cutoffDate.setDate(cutoffDate.getDate() + timeValue);
        }

        // Filter appointments based on criteria
        const matchingCustomerIds = new Set();

        appointmentsSnapshot.forEach((doc) => {
          const appointment = doc.data();

          // Check if appointment belongs to this salon
          if (appointment.salonId !== salonId) return;

          // Check dateTimeRequested is within the range
          const appointmentDate = appointment.dateTimeRequested?.toDate
            ? appointment.dateTimeRequested.toDate()
            : new Date(appointment.dateTimeRequested);

          if (appointmentDate < now || appointmentDate > cutoffDate) return;

          // Check service filter
          if (formData.targetingCriteria.appointmentService !== 'not_selected') {
            if (appointment.serviceId !== formData.targetingCriteria.appointmentService &&
                appointment.service !== formData.targetingCriteria.appointmentService) return;
          }

          // Check staff filter
          if (formData.targetingCriteria.appointmentStaff !== 'not_selected') {
            if (appointment.staffId !== formData.targetingCriteria.appointmentStaff &&
                appointment.staff !== formData.targetingCriteria.appointmentStaff) return;
          }

          // Check status filter
          if (formData.targetingCriteria.appointmentStatus.length > 0) {
            if (!formData.targetingCriteria.appointmentStatus.includes(appointment.status)) return;
          }

          // Add customer ID to the matching set
          if (appointment.customerId) {
            matchingCustomerIds.add(appointment.customerId);
          }
        });

        // Filter customers based on matching appointment customer IDs
        targetedCustomers = customers.filter(customer =>
          matchingCustomerIds.has(customer.id)
        );
      }
      else if (formData.targetingType === 'specific_customer') {
        const selectedCustomerIds = formData.targetingCriteria.specificCustomers.map(c => c.id);
        targetedCustomers = customers.filter(customer =>
          selectedCustomerIds.includes(customer.id)
        );
      }
      else if (formData.targetingType === 'customer_notes') { // #Revised UI_v2
        const searchText = (formData.targetingCriteria.notesSearchText || '').trim().toLowerCase();
        if (searchText) {
          const matchType = formData.targetingCriteria.notesMatchType || 'contains_all';
          targetedCustomers = customers.filter(customer => {
            const notes = (customer.customer_notes || '').toLowerCase();
            if (!notes) return false;
            if (matchType === 'exact') return notes.includes(searchText);
            const words = searchText.split(/[\s,]+/).filter(w => w.length > 0);
            if (matchType === 'contains_any') return words.some(word => notes.includes(word));
            return words.every(word => notes.includes(word)); // contains_all
          });
        }
      }

      console.log(`Targeted ${targetedCustomers.length} customers after filtering`);

      // Return full customer data for personalization, plus diagnostic counts
      return { customers: targetedCustomers, pushOptedIn: pushOptedIn.length };
    } catch (error) {
      console.error('Error getting targeted customers:', error);
      throw error;
    }
  };

  const handleSendTestPushBlast = async () => {
    if (!validatePushCampaign()) {
      showValidationErrors(pushErrors);
      return;
    }

    try {
      setSaving('test');
      setError('');

      const adminUsers = await getAdminUsers();

      if (adminUsers.length === 0) {
        throw new Error('No admin users found for this salon');
      }

      let scheduledFor;
      if (formData.schedulingType === 'one-time') {
        scheduledFor = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
      } else {
        scheduledFor = new Date(`${formData.recurring.startDate}T${formData.scheduledTime}`);
      }

      const campaignData = {
        body: formData.body.trim(),
        imageUrl: formData.imageUrl.trim() || null,
        createdAt: new Date(),
        failureCount: 0,
        schedulingType: formData.schedulingType,
        recurring: formData.schedulingType === 'recurring' ? formData.recurring : null,
        metadata: {
          campaignType: 'test_blast',
          createdBy: currentUser?.uid || '',
          createdByEmail: currentUser?.email || '',
          createdByName: currentUser?.displayName || currentUser?.name || 'Admin User',
          salonId: salonId,
          scheduledDate: formData.schedulingType === 'one-time' ? formData.scheduledDate : null,
          scheduledTime: formData.scheduledTime,
          schedulingType: formData.schedulingType,
          recurring: formData.schedulingType === 'recurring' ? formData.recurring : null,
          targetingCriteria: formData.targetingCriteria,
          targetingType: formData.targetingType,
          timestamp: new Date().toISOString()
        },
        salonId: salonId,
        scheduledFor: scheduledFor,
        sentAt: new Date(),
        status: 'sent',
        successCount: adminUsers.length,
        targetRecipients: adminUsers.map(user => ({
          email: user.email || '',
          fcmToken: user.fcmToken || '',
          name: user.name || user.displayName || 'Admin User',
          role: user.role || 'admin',
          type: 'admin',
          uid: user.uid || ''
        })),
        title: businessName ? `${businessName} | ${formData.subject.trim()}` : formData.subject.trim(),
        type: 'push_notification'
      };

      console.log('Sending test push campaign:', campaignData);

      await addDoc(collection(db, 'pushNotifications'), campaignData);

      setSuccess(`Test push notification sent to ${adminUsers.length} admin(s)`);
      setTimeout(() => setSuccess(''), 5000);

    } catch (err) {
      console.error('Error sending test push blast:', err);
      setError('Failed to send test push blast: ' + err.message);
    } finally {
      setSaving('');
    }
  };

  const handleSendActualPushBlast = async () => {
    if (!validatePushCampaign()) {
      showValidationErrors(pushErrors);
      return;
    }

    try {
      setSaving('actual');
      setError('');

      const { customers: targetedCustomersRaw, pushOptedIn } = await getTargetedCustomers();
      let targetedCustomers = targetedCustomersRaw;

      if (targetedCustomers.length === 0) {
        if (pushOptedIn > 0) {
          throw new Error(
            `${pushOptedIn} customer(s) have push notifications enabled, but none have a valid FCM token. ` +
            'Customers must open the customer portal in their browser and grant notification permission at least once before they can receive push campaigns.'
          );
        }
        throw new Error('No customers match the targeting criteria or all matching customers have push notifications disabled');
      }

      // Apply frequency exclusion before sending
      const { eligible, excluded } = await applyFrequencyExclusionToList(salonId, 'push', targetedCustomers);
      targetedCustomers = eligible;
      if (targetedCustomers.length === 0) {
        throw new Error(`All ${excluded.length} targeted customer(s) have been excluded by frequency limits. Try again later or adjust your frequency settings.`);
      }

      let scheduledFor;
      if (formData.schedulingType === 'one-time') {
        scheduledFor = new Date(`${formData.scheduledDate}T${formData.scheduledTime}`);
      } else {
        scheduledFor = new Date(`${formData.recurring.startDate}T${formData.scheduledTime}`);
      }

      // Generate a shared batch ID so all docs from this campaign can be grouped for attribution
      const campaignBatchId = `campaign_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      const campaignTimestamp = new Date().toISOString();

      // Check if message contains personalization placeholders
      const hasPersonalization = formData.subject.includes('{') || formData.body.includes('{');

      if (hasPersonalization) {
        // Create personalized push notifications for each customer
        const pushPromises = targetedCustomers.map(async (customer) => {
          // Get customer's latest appointment and transaction for personalization
          const [appointment, transaction] = await Promise.all([
            getCustomerAppointment(customer.id, salonId),
            getCustomerTransaction(customer.id, salonId)
          ]);

          // Personalize title and body
          const personalizedTitle = await personalizeMessage(formData.subject, customer, {
            promotional: formData.messageCategory === 'promotional',
            salonId,
            salonName: businessName,
            appointment,
            transaction,
            salonPhone,
            services: salonServices,
            staffList: salonStaff
          });

          const personalizedBody = await personalizeMessage(formData.body, customer, {
            promotional: formData.messageCategory === 'promotional',
            salonId,
            salonName: businessName,
            appointment,
            transaction,
            salonPhone,
            services: salonServices,
            staffList: salonStaff
          });

          const campaignData = {
            body: personalizedBody,
            imageUrl: formData.imageUrl.trim() || null,
            createdAt: new Date(),
            failureCount: 0,
            schedulingType: formData.schedulingType,
            recurring: formData.schedulingType === 'recurring' ? formData.recurring : null,
            metadata: {
              campaignType: 'actual_blast',
              createdBy: currentUser?.uid || '',
              createdByEmail: currentUser?.email || '',
              createdByName: currentUser?.displayName || currentUser?.name || 'Admin User',
              salonId: salonId,
              scheduledDate: formData.schedulingType === 'one-time' ? formData.scheduledDate : null,
              scheduledTime: formData.scheduledTime,
              schedulingType: formData.schedulingType,
              recurring: formData.schedulingType === 'recurring' ? formData.recurring : null,
              targetingCriteria: formData.targetingCriteria,
              targetingType: formData.targetingType,
              timestamp: campaignTimestamp,
              campaignBatchId: campaignBatchId,
              customerId: customer.id,
              customerName: customer.displayName,
              conversionPeriod: Number(formData.conversionPeriod) || 30
            },
            salonId: salonId,
            scheduledFor: scheduledFor,
            sentAt: null,
            status: 'scheduled',
            successCount: 0,
            targetRecipients: [{
              email: customer.email || '',
              fcmToken: customer.fcmToken || '',
              name: customer.displayName || customer.name || 'Customer',
              role: 'customer',
              type: 'customer',
              uid: customer.uid || customer.id || ''
            }],
            title: businessName ? `${businessName} | ${personalizedTitle}` : personalizedTitle,
            type: 'push_notification'
          };

          return addDoc(collection(db, 'pushNotifications'), campaignData);
        });

        await Promise.all(pushPromises);
      } else {
        // No personalization - send one push notification to all recipients
        const targetRecipients = targetedCustomers.map(customer => ({
          email: customer.email || '',
          fcmToken: customer.fcmToken || '',
          name: customer.displayName || customer.name || 'Customer',
          role: 'customer',
          type: 'customer',
          uid: customer.uid || customer.id || ''
        }));

        const campaignData = {
          body: formData.body.trim(),
          imageUrl: formData.imageUrl.trim() || null,
          createdAt: new Date(),
          failureCount: 0,
          schedulingType: formData.schedulingType,
          recurring: formData.schedulingType === 'recurring' ? formData.recurring : null,
          metadata: {
            campaignType: 'actual_blast',
            createdBy: currentUser?.uid || '',
            createdByEmail: currentUser?.email || '',
            createdByName: currentUser?.displayName || currentUser?.name || 'Admin User',
            salonId: salonId,
            scheduledDate: formData.schedulingType === 'one-time' ? formData.scheduledDate : null,
            scheduledTime: formData.scheduledTime,
            schedulingType: formData.schedulingType,
            recurring: formData.schedulingType === 'recurring' ? formData.recurring : null,
            targetingCriteria: formData.targetingCriteria,
            targetingType: formData.targetingType,
            timestamp: campaignTimestamp,
            campaignBatchId: campaignBatchId,
            conversionPeriod: Number(formData.conversionPeriod) || 30
          },
          salonId: salonId,
          scheduledFor: scheduledFor,
          sentAt: null,
          status: 'scheduled',
          successCount: 0,
          targetRecipients: targetRecipients,
          title: businessName ? `${businessName} | ${formData.subject.trim()}` : formData.subject.trim(),
          type: 'push_notification'
        };

        console.log('Scheduling push campaign:', campaignData);
        console.log(`Campaign will target ${targetRecipients.length} customers`);

        await addDoc(collection(db, 'pushNotifications'), campaignData);
      }

      // Record deliveries for frequency tracking
      await recordCampaignDeliveries(salonId, 'push', targetedCustomers);

      let successMessage;
      if (formData.schedulingType === 'one-time') {
        successMessage = `Push campaign scheduled for ${formData.scheduledDate} at ${formData.scheduledTime} - will target ${targetedCustomers.length} customers`;
      } else {
        const endInfo = formData.recurring.endType === 'date'
          ? `until ${formData.recurring.endDate}`
          : `for ${formData.recurring.numberOfCycles} cycles`;
        successMessage = `Recurring push campaign scheduled (${formData.recurring.frequency}) starting ${formData.recurring.startDate} at ${formData.scheduledTime} ${endInfo} - will target ${targetedCustomers.length} customers`;
      }

      if (excluded.length > 0) {
        successMessage += ` (${excluded.length} customer(s) excluded by frequency limits)`;
      }

      setSuccess(successMessage);
      setTimeout(() => setSuccess(''), 5000);

      resetPushForm();

    } catch (err) {
      console.error('Error scheduling push blast:', err);
      setError('Failed to schedule push blast: ' + err.message);
    } finally {
      setSaving('');
    }
  };

  const resetPushForm = () => {
    setFormData({
      subject: '',
      body: '',
      imageUrl: '',
      schedulingType: 'one-time',
      scheduledDate: '',
      scheduledTime: '',
      conversionPeriod: 30,
      recurring: {
        frequency: 'daily',
        dayOfWeek: '',
        dayOfMonth: 0,
        monthOfYear: '',
        dayOfYear: 0,
        startDate: '',
        endDate: '',
        endType: 'date',
        numberOfCycles: 0,
        excludePreviousRecipients: false,
        exclusionType: 'cycles',
        excludePastCycles: 0,
        excludeBeforeDate: ''
      },
      targetingType: 'not_selected',
      targetingCriteria: {
        birthMonths: [],
        postalCodes: [],
        lastTransactionMonths: 0,
        lastTransactionUnit: 'months',
        transactionCount: 0,
        transactionType: 'not_selected',
        transactionAmountType: 'not_selected',
        transactionAmount: 0,
        transactionLocationId: 'not_selected',
        memberTiers: [],
        pointsType: 'not_selected',
        pointsAmount: 0,
        specificCustomers: [],
        notesSearchText: '', // #Revised UI_v2 — customer notes targeting
        notesMatchType: 'contains_all', // #Revised UI_v2 — customer notes targeting
        appointmentTimeValue: 0,
        appointmentTimeUnit: 'days',
        appointmentService: 'not_selected',
        appointmentStaff: 'not_selected',
        appointmentStatus: []
      }
    });
    setPushErrors({});
    setFieldErrors({});
    setSelectedCustomers([]);
    setCustomerSearchQuery('');
    setCustomerSearchResults([]);
    setImagePreview('');
    setImageError('');
    setTargetingCriteriaSaved(false); // NEW: Reset saved state
  };

  const handleInputChange = (field, value) => {
    // NEW: Reset targeting criteria saved state and clear criteria when targeting type changes
    if (field === 'targetingType') {
      setTargetingCriteriaSaved(false);
      setFormData(prev => ({
        ...prev,
        [field]: value,
        targetingCriteria: {
          birthMonths: [],
          postalCodes: [],
          lastTransactionMonths: 0,
          lastTransactionUnit: 'months',
          transactionCount: 0,
          transactionType: 'not_selected',
          transactionAmountType: 'not_selected',
          transactionAmount: 0,
          transactionLocationId: 'not_selected',
          memberTiers: [],
          pointsType: 'not_selected',
          pointsAmount: 0,
          specificCustomers: [],
          notesSearchText: '', // #Revised UI_v2 — customer notes targeting
          notesMatchType: 'contains_all', // #Revised UI_v2 — customer notes targeting
          appointmentTimeValue: 0,
          appointmentTimeUnit: 'days',
          appointmentService: 'not_selected',
          appointmentStaff: 'not_selected',
          appointmentStatus: []
        }
      }));
      setSelectedCustomers([]);

      if (pushErrors[field]) {
        setPushErrors(prev => ({
          ...prev,
          [field]: ''
        }));
      }
      if (fieldErrors[field]) {
        setFieldErrors(prev => ({
          ...prev,
          [field]: ''
        }));
      }
      return;
    }

    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Validate mandatory fields on change
    if (['subject', 'body', 'scheduledDate', 'scheduledTime'].includes(field)) {
      validateField(field, value);
    }

    if (pushErrors[field]) {
      setPushErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  useEffect(() => {
    const pendingData = sessionStorage.getItem('pendingNotificationData');
    if (pendingData) {
      try {
        const notificationData = JSON.parse(pendingData);
        
        if (notificationData.type === 'push') {
          setFormData({
            ...formData,
            subject: notificationData.subject,
            body: notificationData.body,
            imageUrl: notificationData.imageUrl || '',
            targetingType: notificationData.targetingType,
            targetingCriteria: notificationData.targetingCriteria
          });
          
          if (notificationData.imageUrl) {
            setImagePreview(notificationData.imageUrl);
          }
          
          sessionStorage.removeItem('pendingNotificationData');
        }
      } catch (error) {
        console.error('Error parsing pending notification data:', error);
      }
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.customer-search-container')) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const handleTargetingCriteriaChange = (field, value) => {
    setFormData({
      ...formData,
      targetingCriteria: {
        ...formData.targetingCriteria,
        [field]: value
      }
    });
    // NEW: Mark as unsaved when criteria changes
    setTargetingCriteriaSaved(false);
  };
  
  const handlePostalCodesChange = (e) => {
    const codes = e.target.value.split(',').map(code => code.trim()).filter(code => code);
    handleTargetingCriteriaChange('postalCodes', codes);
  };

  const handleBirthMonthsChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    handleTargetingCriteriaChange('birthMonths', selectedOptions);
  };

  const handleMemberTiersChange = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
    handleTargetingCriteriaChange('memberTiers', selectedOptions);
  };

  const insertEmoji = (emoji) => {
    const textarea = document.getElementById('pushSubject');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = formData.subject.substring(0, start) + emoji + formData.subject.substring(end);
      handleInputChange('subject', newValue);
      
      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
        textarea.focus();
      }, 0);
    }
  };

  const insertEmojiInBody = (emoji) => {
    const textarea = document.getElementById('pushBody');
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newValue = formData.body.substring(0, start) + emoji + formData.body.substring(end);
      handleInputChange('body', newValue);

      setTimeout(() => {
        textarea.selectionStart = textarea.selectionEnd = start + emoji.length;
        textarea.focus();
      }, 0);
    }
  };

  const handleRecurringChange = (field, value) => {
    setFormData({
      ...formData,
      recurring: {
        ...formData.recurring,
        [field]: value
      }
    });
  };
  
  return (
    <div className="notifications-editor">
      <h2>Push Notification Campaigns</h2>

      {/* Sub-tab Navigation */}
      <div className="sub-tab-navigation">
        <button
          className={`sub-tab-button ${activeSubTab === 'campaign-frequency' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('campaign-frequency')}
        >
          Campaign Frequency
        </button>
        <button
          className={`sub-tab-button ${activeSubTab === 'create-campaign' ? 'active' : ''}`}
          onClick={() => setActiveSubTab('create-campaign')}
        >
          Create Campaign
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}
      
      {previewLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <div className="loading-text">Loading customer preview...</div>
          </div>
        </div>
      )}
      
      <CustomerPreviewModal
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        previewData={previewData}
        campaignName={formData.subject || 'push campaign'}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay">
          <div className="delete-confirm-modal">
            <div className="modal-header">
              <h3>Delete Template</h3>
              <button className="modal-close-btn" onClick={() => setShowDeleteConfirm(false)} aria-label="Close">
                ×
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this template?</p>
              <p className="delete-warning">
                <strong>{selectedTemplate?.title}</strong>
              </p>
              <p>This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button
                className="cancel-button"
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="delete-confirm-button"
                onClick={confirmDeleteTemplate}
              >
                Delete Template
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Push Campaign Frequency Control */}
      {activeSubTab === 'campaign-frequency' && (
        <CampaignFrequencyControl salonId={salonId} campaignType="push" />
      )}

      {/* Push Notification Campaign Templates Section */}
      {activeSubTab === 'create-campaign' && (
        <>
      <div className="templates-section">
        <h3>Push Notification Campaign Templates</h3>
        <p className="templates-description">Choose a pre-built template to get started quickly. Click on a template and then "Create Campaign" to populate the form.</p>

        <div className="template-category-tabs">
          <button
            className={`template-tab ${selectedTemplateCategory === 'transactional' ? 'active' : ''}`}
            onClick={() => setSelectedTemplateCategory('transactional')}
          >
            Transactional Campaigns
          </button>
          <button
            className={`template-tab ${selectedTemplateCategory === 'promotional' ? 'active' : ''}`}
            onClick={() => setSelectedTemplateCategory('promotional')}
          >
            Promotional Campaigns
          </button>
        </div>

        <div className="templates-grid">
          {getAllTemplates(selectedTemplateCategory).map((template) => (
            <div
              key={template.id}
              className={`template-card ${selectedTemplate?.id === template.id ? 'selected' : ''} ${template.isCustom ? 'custom-template' : ''}`}
              onClick={() => setSelectedTemplate(template)}
            >
              <div className="template-card-header">
                <div className="template-title">
                  {template.title}
                  {template.isCustom && <span className="custom-badge">Custom</span>}
                </div>
                {template.messageType !== 'not_selected' && (
                  <div className="template-badge">{template.messageType.replace('_', ' ')}</div>
                )}
              </div>
              <div className="template-preview">{template.body.substring(0, 100)}...</div>
              {selectedTemplate?.id === template.id && (
                <div className="template-actions">
                  {template.messageCategory !== 'transactional' && (
                    <button
                      className="template-load-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleLoadTemplate(template);
                      }}
                    >
                      Create Campaign
                    </button>
                  )}
                  {template.messageCategory === 'transactional' && (
                    <button
                      className={campaignSettings[`push_${template.id}`] !== false ? 'template-delete-button' : 'template-load-button'}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleCampaign(template);
                      }}
                    >
                      {campaignSettings[`push_${template.id}`] !== false ? 'Disable Campaign' : 'Enable Campaign'}
                    </button>
                  )}
                  <button
                    className="template-revise-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleReviseTemplate(template);
                    }}
                  >
                    Revise Template
                  </button>
                  {template.isCustom && (
                    <button
                      className="template-delete-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteTemplate();
                      }}
                    >
                      Delete Template
                    </button>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="notification-form">
        <h3>Create Push Campaign</h3>
        
        <div className="form-section">
          <h4>Title</h4>
          
          <div className="form-group">
            <label htmlFor="pushSubject">
              Push Title<span className="required-asterisk">*</span>
            </label>
            <div className="input-with-emojis">
              <input
                type="text"
                id="pushSubject"
                name="subject"
                value={formData.subject}
                onChange={(e) => handleInputChange('subject', e.target.value)}
                onBlur={handleFieldBlur}
                className={fieldErrors.subject ? 'input-error' : ''}
                maxLength={50}
                required
              />
              {fieldErrors.subject && <span className="field-error-message">{fieldErrors.subject}</span>}
              <div className="emoji-picker">
                <span>Add emoji:</span>
                {commonEmojis.map(emoji => (
                  <button 
                    key={emoji} 
                    type="button" 
                    className="emoji-button"
                    onClick={() => insertEmoji(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-helper-text">
              Max 50 characters - keep it short and attention-grabbing
            </div>
          </div>
        </div>
        
        <div className="form-section">
          <h4>Message Content</h4>
          
          <div className="form-group">
            <label htmlFor="pushBody">
              Message Body<span className="required-asterisk">*</span>
            </label>
            <div className="textarea-with-emojis">
              <textarea
                id="pushBody"
                name="body"
                value={formData.body}
                onChange={(e) => handleInputChange('body', e.target.value)}
                onBlur={handleFieldBlur}
                rows="6"
                placeholder="Write your push notification message here...

Keep it concise and engaging.
Max 200 characters."
                className={fieldErrors.body ? 'input-error' : ''}
                maxLength={200}
                required
              />
              {fieldErrors.body && <span className="field-error-message">{fieldErrors.body}</span>}
              <div className="emoji-picker">
                <span>Add emoji:</span>
                {commonEmojis.map(emoji => (
                  <button 
                    key={emoji} 
                    type="button" 
                    className="emoji-button"
                    onClick={() => insertEmojiInBody(emoji)}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
            <div className="form-helper-text">
              Max 200 characters - clear and concise messaging works best for push notifications.
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="pushImageUrl">
              Hero Image/GIF URL (Optional)
              {pushErrors.imageUrl && <span className="error-text"> - {pushErrors.imageUrl}</span>}
            </label>
            <div className="image-url-input-container">
              <input
                type="url"
                id="pushImageUrl"
                name="imageUrl"
                value={formData.imageUrl}
                onChange={(e) => handleImageUrlChange(e.target.value)}
                placeholder="https://example.com/image.jpg or https://giphy.com/..."
                className={pushErrors.imageUrl || imageError ? 'error' : ''}
              />
              {formData.imageUrl && (
                <button 
                  type="button" 
                  className="clear-image-button"
                  onClick={clearImage}
                  title="Clear image"
                >
                  ✕
                </button>
              )}
            </div>
            <div className="form-helper-text">
              Add a banner image or GIF. Paste a direct link to a jpg/png or a Giphy/Tenor GIF URL.
            </div>
            <div className="form-helper-text" style={{ marginTop: '6px', padding: '8px 10px', background: '#fff8e1', borderRadius: '6px', borderLeft: '3px solid #f9a825', color: '#5d4037' }}>
              <strong>GIF animation note:</strong> When the customer has the app open, GIFs play with full animation in an in-app popup. When the app is closed, the OS notification banner always shows only the first frame — this is a system-level limitation on all devices and cannot be changed.
            </div>
            {imageError && (
              <div className="image-error-message">
                {imageError}
              </div>
            )}
          </div>

          {/* Full message preview — always visible when there is content */}
          {(formData.subject || formData.body) && (
            <div style={{ marginTop: '20px' }}>
              <h5 style={{ margin: '0 0 10px', fontSize: '14px', color: '#555', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Message Preview</h5>
              {/* Device selector pills */}
              <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
                <button type="button" onClick={() => setPreviewDevice('mobile')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '4px 12px', borderRadius: '20px', border: `1.5px solid ${previewDevice === 'mobile' ? '#c47a6a' : '#ddd'}`, background: previewDevice === 'mobile' ? '#fdf0ee' : '#fafafa', color: previewDevice === 'mobile' ? '#c47a6a' : '#888', fontWeight: previewDevice === 'mobile' ? '600' : '400', fontSize: '12px', cursor: 'pointer', transition: 'all 0.15s' }}>
                  <svg width="10" height="14" viewBox="0 0 10 14" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="9" height="13" rx="1.5" stroke="currentColor" strokeWidth="1"/><rect x="3.5" y="11" width="3" height="1" rx="0.5" fill="currentColor"/></svg>
                  Mobile
                </button>
                <button type="button" onClick={() => setPreviewDevice('laptop')} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '4px 12px', borderRadius: '20px', border: `1.5px solid ${previewDevice === 'laptop' ? '#c47a6a' : '#ddd'}`, background: previewDevice === 'laptop' ? '#fdf0ee' : '#fafafa', color: previewDevice === 'laptop' ? '#c47a6a' : '#888', fontWeight: previewDevice === 'laptop' ? '600' : '400', fontSize: '12px', cursor: 'pointer', transition: 'all 0.15s' }}>
                  <svg width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="0.5" y="0.5" width="13" height="8" rx="1" stroke="currentColor" strokeWidth="1"/><rect x="2" y="2" width="10" height="5.5" rx="0.3" fill="currentColor" opacity="0.18"/><rect x="0" y="9" width="14" height="1.5" rx="0.75" fill="currentColor" opacity="0.5"/></svg>
                  Laptop
                </button>
              </div>
              {/* Device frame */}
              <div style={{ display: 'flex', justifyContent: 'center', overflowX: 'auto' }}>
                {previewDevice === 'mobile' ? (
                  /* Realistic iPhone frame — 86px wide, 495px screen height */
                  <div style={{ position: 'relative', display: 'inline-block', flexShrink: 0 }}>
                    {/* Mute switch */}
                    <div style={{ position: 'absolute', left: '-3px', top: '58px', width: '3px', height: '11px', background: '#3a3a3a', borderRadius: '2px 0 0 2px', boxShadow: 'inset 1px 0 0 rgba(255,255,255,0.1)' }} />
                    {/* Volume up */}
                    <div style={{ position: 'absolute', left: '-3px', top: '76px', width: '3px', height: '20px', background: '#3a3a3a', borderRadius: '2px 0 0 2px', boxShadow: 'inset 1px 0 0 rgba(255,255,255,0.1)' }} />
                    {/* Volume down */}
                    <div style={{ position: 'absolute', left: '-3px', top: '102px', width: '3px', height: '20px', background: '#3a3a3a', borderRadius: '2px 0 0 2px', boxShadow: 'inset 1px 0 0 rgba(255,255,255,0.1)' }} />
                    {/* Power button */}
                    <div style={{ position: 'absolute', right: '-3px', top: '82px', width: '3px', height: '28px', background: '#3a3a3a', borderRadius: '0 2px 2px 0', boxShadow: 'inset -1px 0 0 rgba(255,255,255,0.1)' }} />
                    {/* iPhone body */}
                    <div style={{ width: '224px', background: 'linear-gradient(170deg,#2d2d2d 0%,#1a1a1a 50%,#232323 100%)', borderRadius: '22px', overflow: 'hidden', boxShadow: '0 0 0 1px rgba(255,255,255,0.06), 0 22px 55px rgba(0,0,0,0.65), inset 0 1px 0 rgba(255,255,255,0.1)' }}>
                      {/* Top bezel + Dynamic Island */}
                      <div style={{ background: '#000', paddingTop: '10px', paddingBottom: '5px', display: 'flex', justifyContent: 'center' }}>
                        <div style={{ width: '50px', height: '14px', background: '#0d0d0d', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '5px', boxShadow: '0 0 0 0.5px #222' }}>
                          <div style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#151515', boxShadow: '0 0 0 0.5px #2a2a2a, inset 0 0 2px rgba(0,100,200,0.25)' }} />
                        </div>
                      </div>
                      {/* Screen — 495px, vertical scroll only */}
                      <div style={{ height: '362px', overflowY: 'auto', overflowX: 'hidden', scrollbarWidth: 'thin', background: 'linear-gradient(160deg,#2c3e7a,#7c4a8a)', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
                        <div style={{ padding: '10px 7px 8px' }}>
                          <div style={{ textAlign: 'center', marginBottom: '12px' }}>
                            <div style={{ fontSize: '28px', fontWeight: '200', color: '#fff', lineHeight: 1 }}>9:41</div>
                            <div style={{ fontSize: '8px', color: 'rgba(255,255,255,0.65)', marginTop: '2px' }}>Saturday, Jan 1</div>
                          </div>
                          <div style={{ background: 'rgba(242,242,247,0.85)', borderRadius: '12px', padding: '8px 9px', backdropFilter: 'blur(12px)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '4px' }}>
                              <div style={{ width: '14px', height: '14px', borderRadius: '4px', background: 'linear-gradient(135deg,#e8a598,#c47a6a)', flexShrink: 0 }} />
                              <div style={{ fontSize: '8px', fontWeight: '600', color: '#555', textTransform: 'uppercase', letterSpacing: '0.3px', flex: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{businessName || 'Bespoke Beauty'}</div>
                              <div style={{ fontSize: '7.5px', color: '#aaa', flexShrink: 0 }}>now</div>
                            </div>
                            <div style={{ fontSize: '9px', fontWeight: '700', color: '#111', marginBottom: '3px', wordBreak: 'break-word' }}>
                              {formData.subject || <span style={{ fontStyle: 'italic', fontWeight: '400', color: '#bbb' }}>Message title…</span>}
                            </div>
                            <div style={{ fontSize: '8.5px', color: '#444', lineHeight: '1.4', wordBreak: 'break-word' }}>
                              {formData.body || <span style={{ fontStyle: 'italic', color: '#bbb' }}>Message body…</span>}
                            </div>
                            {imagePreview && !imageError && (
                              <img src={imagePreview} alt="" onError={handleImageLoadError} style={{ width: '100%', marginTop: '6px', borderRadius: '7px', objectFit: 'cover', display: 'block' }} />
                            )}
                          </div>
                        </div>
                      </div>
                      {/* Bottom bezel + home indicator */}
                      <div style={{ background: '#000', paddingTop: '6px', paddingBottom: '12px', display: 'flex', justifyContent: 'center' }}>
                        <div style={{ width: '40px', height: '4px', background: 'rgba(255,255,255,0.22)', borderRadius: '3px' }} />
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Laptop frame — 2× width (560px) and 2× screen height (316px), scrollable */
                  <div style={{ display: 'inline-block', flexShrink: 0 }}>
                    <div style={{ width: '483px', background: '#1c1c1e', borderRadius: '8px 8px 0 0', padding: '8px 7px 0', boxSizing: 'border-box', boxShadow: 'inset 0 0 0 1px #3a3a3a' }}>
                      <div style={{ width: '4px', height: '4px', background: '#444', borderRadius: '50%', margin: '0 auto 4px' }} />
                      <div style={{ background: '#1e1e2e', borderRadius: '3px 3px 0 0', height: '276px', overflowY: 'auto', scrollbarWidth: 'thin', position: 'relative', fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}>
                        <div style={{ background: 'rgba(255,255,255,0.08)', padding: '3px 8px', display: 'flex', justifyContent: 'space-between', position: 'sticky', top: 0, zIndex: 1 }}>
                          <div style={{ fontSize: '7px', color: 'rgba(255,255,255,0.7)', fontWeight: '600' }}>&#63743; File Edit View</div>
                          <div style={{ fontSize: '7px', color: 'rgba(255,255,255,0.6)' }}>9:41 AM</div>
                        </div>
                        <div style={{ padding: '10px 10px 10px', display: 'flex', justifyContent: 'flex-end' }}>
                          <div style={{ width: '140px', background: 'rgba(38,38,48,0.95)', borderRadius: '10px', padding: '8px 10px', boxShadow: '0 4px 18px rgba(0,0,0,0.6)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '5px', marginBottom: '4px' }}>
                              <div style={{ width: '12px', height: '12px', borderRadius: '3px', background: 'linear-gradient(135deg,#e8a598,#c47a6a)', flexShrink: 0 }} />
                              <div style={{ fontSize: '7.5px', fontWeight: '600', color: '#999', textTransform: 'uppercase', letterSpacing: '0.3px', flex: 1, overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>{businessName || 'Bespoke Beauty'}</div>
                            </div>
                            <div style={{ fontSize: '9.5px', fontWeight: '700', color: '#fff', marginBottom: '3px', wordBreak: 'break-word' }}>
                              {formData.subject || <span style={{ fontStyle: 'italic', fontWeight: '400', color: '#888' }}>Message title…</span>}
                            </div>
                            <div style={{ fontSize: '8.5px', color: '#ccc', lineHeight: '1.4', wordBreak: 'break-word' }}>
                              {formData.body || <span style={{ fontStyle: 'italic', color: '#666' }}>Message body…</span>}
                            </div>
                            {imagePreview && !imageError && (
                              <img src={imagePreview} alt="" onError={handleImageLoadError} style={{ width: '100%', marginTop: '5px', borderRadius: '4px', objectFit: 'cover', display: 'block' }} />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div style={{ width: '483px', height: '10px', background: '#2c2c2e' }} />
                    <div style={{ width: '515px', height: '8px', background: '#1c1c1e', borderRadius: '0 0 6px 6px', marginLeft: '-16px', boxShadow: '0 2px 6px rgba(0,0,0,0.3)' }} />
                  </div>
                )}
              </div>
              <div className="form-helper-text" style={{ marginTop: '10px', textAlign: 'center' }}>
                Preview of how the notification will appear on a recipient's {previewDevice === 'mobile' ? 'phone' : 'desktop'}.
              </div>
            </div>
          )}
        </div>
        
        <div className={`form-section ${templateEditMode ? 'disabled-section' : ''}`}>
          <h4>Scheduling</h4>

          <div className="form-group">
            <label>Campaign Type *</label>
            <div className="scheduling-type-tabs">
              <button
                type="button"
                className={`scheduling-tab ${formData.schedulingType === 'one-time' ? 'active' : ''}`}
                onClick={() => handleInputChange('schedulingType', 'one-time')}
                disabled={templateEditMode}
              >
                One-Time Campaign
              </button>
              <button
                type="button"
                className={`scheduling-tab ${formData.schedulingType === 'recurring' ? 'active' : ''}`}
                onClick={() => handleInputChange('schedulingType', 'recurring')}
                disabled={templateEditMode}
              >
                Recurring Campaign
              </button>
            </div>
          </div>

          {formData.schedulingType === 'one-time' ? (
            <div className="one-time-scheduling">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="scheduledDate">
                    Scheduled Date<span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="date"
                    id="scheduledDate"
                    name="scheduledDate"
                    value={formData.scheduledDate}
                    onChange={(e) => handleInputChange('scheduledDate', e.target.value)}
                    onBlur={handleFieldBlur}
                    min={new Date().toISOString().split('T')[0]}
                    className={fieldErrors.scheduledDate ? 'input-error' : ''}
                    disabled={templateEditMode}
                    required
                  />
                  {fieldErrors.scheduledDate && <span className="field-error-message">{fieldErrors.scheduledDate}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="scheduledTime">
                    Scheduled Time<span className="required-asterisk">*</span>
                  </label>
                  <input
                    type="time"
                    id="scheduledTime"
                    name="scheduledTime"
                    value={formData.scheduledTime}
                    onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
                    onBlur={handleFieldBlur}
                    className={fieldErrors.scheduledTime ? 'input-error' : ''}
                    disabled={templateEditMode}
                    required
                  />
                  {fieldErrors.scheduledTime && <span className="field-error-message">{fieldErrors.scheduledTime}</span>}
                </div>
              </div>

              {pushErrors.scheduledDateTime && (
                <div className="error-message">
                  {pushErrors.scheduledDateTime}
                </div>
              )}
            </div>
          ) : (
            <div className="recurring-scheduling">
              <div className="form-group">
                <label htmlFor="recurringFrequency">
                  Frequency *
                </label>
                <select
                  id="recurringFrequency"
                  value={formData.recurring.frequency}
                  onChange={(e) => handleRecurringChange('frequency', e.target.value)}
                  disabled={templateEditMode}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="annually">Annually</option>
                </select>
              </div>

              {formData.recurring.frequency === 'weekly' && (
                <div className="form-group">
                  <label htmlFor="dayOfWeek">Day of Week *</label>
                  <select
                    id="dayOfWeek"
                    value={formData.recurring.dayOfWeek}
                    onChange={(e) => handleRecurringChange('dayOfWeek', e.target.value)}
                    disabled={templateEditMode}
                  >
                    <option value="monday">Monday</option>
                    <option value="tuesday">Tuesday</option>
                    <option value="wednesday">Wednesday</option>
                    <option value="thursday">Thursday</option>
                    <option value="friday">Friday</option>
                    <option value="saturday">Saturday</option>
                    <option value="sunday">Sunday</option>
                  </select>
                </div>
              )}

              {formData.recurring.frequency === 'monthly' && (
                <div className="form-group">
                  <label htmlFor="dayOfMonth">Day of Month *</label>
                  <input
                    type="number"
                    id="dayOfMonth"
                    min="1"
                    max="31"
                    value={formData.recurring.dayOfMonth}
                    onChange={(e) => handleRecurringChange('dayOfMonth', parseInt(e.target.value))}
                    disabled={templateEditMode}
                  />
                  <div className="form-helper-text">Enter a number between 1-31</div>
                </div>
              )}

              {formData.recurring.frequency === 'annually' && (
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="monthOfYear">Month *</label>
                    <select
                      id="monthOfYear"
                      value={formData.recurring.monthOfYear}
                      onChange={(e) => handleRecurringChange('monthOfYear', e.target.value)}
                      disabled={templateEditMode}
                    >
                      <option value="january">January</option>
                      <option value="february">February</option>
                      <option value="march">March</option>
                      <option value="april">April</option>
                      <option value="may">May</option>
                      <option value="june">June</option>
                      <option value="july">July</option>
                      <option value="august">August</option>
                      <option value="september">September</option>
                      <option value="october">October</option>
                      <option value="november">November</option>
                      <option value="december">December</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label htmlFor="dayOfYear">Day *</label>
                    <input
                      type="number"
                      id="dayOfYear"
                      min="1"
                      max="31"
                      value={formData.recurring.dayOfYear}
                      onChange={(e) => handleRecurringChange('dayOfYear', parseInt(e.target.value))}
                      disabled={templateEditMode}
                    />
                    <div className="form-helper-text">Day of the month (1-31)</div>
                  </div>
                </div>
              )}

              <div className="form-group">
                <label htmlFor="recurringTime">Time *</label>
                <input
                  type="time"
                  id="recurringTime"
                  value={formData.scheduledTime}
                  onChange={(e) => handleInputChange('scheduledTime', e.target.value)}
                  disabled={templateEditMode}
                />
                <div className="form-helper-text">Time when the campaign should run</div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="recurringStartDate">Start Date *</label>
                  <input
                    type="date"
                    id="recurringStartDate"
                    value={formData.recurring.startDate}
                    onChange={(e) => handleRecurringChange('startDate', e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    disabled={templateEditMode}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Campaign End *</label>
                <div className="recurring-end-type">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="endType"
                      value="date"
                      checked={formData.recurring.endType === 'date'}
                      onChange={(e) => handleRecurringChange('endType', e.target.value)}
                      disabled={templateEditMode}
                    />
                    <span>End on specific date</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="endType"
                      value="cycles"
                      checked={formData.recurring.endType === 'cycles'}
                      onChange={(e) => handleRecurringChange('endType', e.target.value)}
                      disabled={templateEditMode}
                    />
                    <span>End after number of cycles</span>
                  </label>
                </div>
              </div>

              {formData.recurring.endType === 'date' ? (
                <div className="form-group">
                  <label htmlFor="recurringEndDate">End Date *</label>
                  <input
                    type="date"
                    id="recurringEndDate"
                    value={formData.recurring.endDate}
                    onChange={(e) => handleRecurringChange('endDate', e.target.value)}
                    min={formData.recurring.startDate || new Date().toISOString().split('T')[0]}
                    disabled={templateEditMode}
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label htmlFor="numberOfCycles">Number of Cycles *</label>
                  <input
                    type="number"
                    id="numberOfCycles"
                    min="1"
                    value={formData.recurring.numberOfCycles}
                    onChange={(e) => handleRecurringChange('numberOfCycles', parseInt(e.target.value))}
                    disabled={templateEditMode}
                  />
                  <div className="form-helper-text">How many times should this campaign run?</div>
                </div>
              )}

              <div className="form-group" style={{ marginTop: '25px', paddingTop: '25px', borderTop: '2px solid #e0e0e0' }}>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.recurring.excludePreviousRecipients}
                    onChange={(e) => handleRecurringChange('excludePreviousRecipients', e.target.checked)}
                    disabled={templateEditMode}
                  />
                  <span>Exclude previous recipients from each campaign run</span>
                </label>
                <div className="form-helper-text">
                  Prevent customers who received this campaign in the past from receiving it again
                </div>
              </div>

              {formData.recurring.excludePreviousRecipients && (
                <div className="exclusion-settings">
                  <div className="form-group">
                    <label>Exclusion Period</label>
                    <div className="recurring-end-type">
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="exclusionType"
                          value="cycles"
                          checked={formData.recurring.exclusionType === 'cycles'}
                          onChange={(e) => handleRecurringChange('exclusionType', e.target.value)}
                          disabled={templateEditMode}
                        />
                        <span>Exclude from past X cycles</span>
                      </label>
                      <label className="radio-label">
                        <input
                          type="radio"
                          name="exclusionType"
                          value="date"
                          checked={formData.recurring.exclusionType === 'date'}
                          onChange={(e) => handleRecurringChange('exclusionType', e.target.value)}
                          disabled={templateEditMode}
                        />
                        <span>Exclude who received before specific date</span>
                      </label>
                    </div>
                  </div>

                  {formData.recurring.exclusionType === 'cycles' ? (
                    <div className="form-group">
                      <label htmlFor="excludePastCycles">
                        Exclude past cycles
                        {pushErrors.excludePastCycles && <span className="error-text"> - {pushErrors.excludePastCycles}</span>}
                      </label>
                      <input
                        type="number"
                        id="excludePastCycles"
                        min="1"
                        value={formData.recurring.excludePastCycles}
                        onChange={(e) => handleRecurringChange('excludePastCycles', parseInt(e.target.value))}
                        disabled={templateEditMode}
                      />
                      <div className="form-helper-text">
                        Exclude customers who received this in the last X campaign runs
                      </div>
                    </div>
                  ) : (
                    <div className="form-group">
                      <label htmlFor="excludeBeforeDate">
                        Exclude before date
                        {pushErrors.excludeBeforeDate && <span className="error-text"> - {pushErrors.excludeBeforeDate}</span>}
                      </label>
                      <input
                        type="date"
                        id="excludeBeforeDate"
                        value={formData.recurring.excludeBeforeDate}
                        onChange={(e) => handleRecurringChange('excludeBeforeDate', e.target.value)}
                        max={new Date().toISOString().split('T')[0]}
                        disabled={templateEditMode}
                      />
                      <div className="form-helper-text">
                        Exclude customers who received campaigns of this type before this date
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className={`form-section ${templateEditMode ? 'disabled-section' : ''}`}>
          <h4>Conversion Tracking</h4>
          <div className="form-group">
            <label htmlFor="conversionPeriod">
              Conversion Period (days)
            </label>
            <input
              type="number"
              id="conversionPeriod"
              name="conversionPeriod"
              min="1"
              max="365"
              value={formData.conversionPeriod}
              onChange={(e) => handleInputChange('conversionPeriod', Math.max(1, parseInt(e.target.value, 10) || 30))}
              disabled={templateEditMode}
              style={{ width: 120 }}
            />
            <div className="form-helper-text">
              Bookings made within this many days of the campaign being sent will be attributed as conversions from this campaign.
            </div>
          </div>
        </div>

        <div className={`form-section ${templateEditMode ? 'disabled-section' : ''}`}>
          <h4>Customer Targeting</h4>

          <div className="form-group">
            <label htmlFor="targetingType">Target Audience<span className="required-asterisk">*</span></label>
            <select
              id="targetingType"
              name="targetingType"
              value={formData.targetingType}
              onChange={(e) => handleInputChange('targetingType', e.target.value)}
              onBlur={handleFieldBlur}
              className={fieldErrors.targetingType ? 'input-error' : ''}
              disabled={templateEditMode}
            >
              <option value="not_selected">Not Selected</option>
              <option value="all_customers">All Customers</option>
              <option value="customer_profile">Customer Profile Based</option>
              <option value="customer_transaction">Customer Transaction Based</option>
              <option value="customer_points">Customer Points Based</option>
              <option value="customer_appointment">Customer Appointment Based</option>
              <option value="specific_customer">Specific Customer</option>
              <option value="customer_notes">Customer Notes Based</option>
            </select>
            {fieldErrors.targetingType && <span className="field-error-message">{fieldErrors.targetingType}</span>}
          </div>

          {formData.targetingType === 'customer_profile' && (
            <div className="targeting-criteria">
              <div className="form-group">
                <label>Birth Months</label>
                <select
                  multiple
                  value={formData.targetingCriteria.birthMonths}
                  onChange={handleBirthMonthsChange}
                  size="6"
                  disabled={templateEditMode}
                >
                  {birthMonthOptions.map(month => (
                    <option key={month} value={month}>{month}</option>
                  ))}
                </select>
                <div className="form-helper-text">
                  Hold Ctrl/Cmd to select multiple months
                </div>
              </div>

              <div className="form-group">
                <label>Postal Codes (comma-separated)</label>
                <input
                  type="text"
                  value={formData.targetingCriteria.postalCodes.join(', ')}
                  onChange={handlePostalCodesChange}
                  placeholder="e.g., 560, 123456, 567890"
                  disabled={templateEditMode}
                />
                <div className="form-helper-text">
                  Leave empty for all postal codes. Use partial codes for broader targeting.
                </div>
              </div>
            </div>
          )}

          {formData.targetingType === 'customer_transaction' && (
            <div className="targeting-criteria">
              {isMultiLocation && (
                <div className="form-group">
                  <label>Location Name</label>
                  <select
                    value={formData.targetingCriteria.transactionLocationId || 'not_selected'}
                    onChange={(e) => handleTargetingCriteriaChange('transactionLocationId', e.target.value)}
                    disabled={templateEditMode}
                  >
                    <option value="not_selected">All Locations</option>
                    {locations.map(loc => (
                      <option key={loc.locationId} value={loc.locationId}>
                        {loc.locationName}{loc.isHeadOffice ? ' (Head Office)' : ''}
                      </option>
                    ))}
                  </select>
                  <div className="form-helper-text">
                    Filter by transactions at a specific location, or leave as "All Locations".
                  </div>
                </div>
              )}

              <div className="form-group">
                <label>Transaction Count Criteria</label>
                <select
                  value={formData.targetingCriteria.transactionType}
                  onChange={(e) => handleTargetingCriteriaChange('transactionType', e.target.value)}
                  disabled={templateEditMode}
                >
                  <option value="not_selected">Not Selected</option>
                  <option value="less_than">Less than X transactions</option>
                  <option value="equal_to">Exactly X transactions</option>
                  <option value="greater_than">More than X transactions</option>
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Number of Transactions</label>
                  <input
                    type="number"
                    value={formData.targetingCriteria.transactionCount}
                    onChange={(e) => handleTargetingCriteriaChange('transactionCount', Number(e.target.value))}
                    min="0"
                    disabled={templateEditMode}
                  />
                </div>
                <div className="form-group">
                  <label>Last Transaction within</label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      type="number"
                      value={formData.targetingCriteria.lastTransactionMonths}
                      onChange={(e) => handleTargetingCriteriaChange('lastTransactionMonths', Number(e.target.value))}
                      min="0"
                      disabled={templateEditMode}
                      style={{ width: '80px' }}
                    />
                    <select
                      value={formData.targetingCriteria.lastTransactionUnit || 'months'}
                      onChange={(e) => handleTargetingCriteriaChange('lastTransactionUnit', e.target.value)}
                      disabled={templateEditMode}
                    >
                      <option value="hours">Hours</option>
                      <option value="days">Days</option>
                      <option value="months">Months</option>
                      <option value="years">Years</option>
                    </select>
                  </div>
                </div>
              </div>

              <div className="form-group">
                <label>Spend Amount Criteria</label>
                <select
                  value={formData.targetingCriteria.transactionAmountType}
                  onChange={(e) => handleTargetingCriteriaChange('transactionAmountType', e.target.value)}
                  disabled={templateEditMode}
                >
                  <option value="not_selected">Not Selected</option>
                  <option value="greater_than">Greater Than the Following Amount</option>
                  <option value="less_than">Less Than the Following Amount</option>
                </select>
              </div>

              <div className="form-group">
                <label>Amount ($)</label>
                <input
                  type="number"
                  value={formData.targetingCriteria.transactionAmount}
                  onChange={(e) => handleTargetingCriteriaChange('transactionAmount', Number(e.target.value))}
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  disabled={templateEditMode}
                />
              </div>
            </div>
          )}

          {formData.targetingType === 'customer_points' && (
            <div className="targeting-criteria">
              <div className="form-group">
                <label>Member Tiers</label>
                <select
                  multiple
                  value={formData.targetingCriteria.memberTiers}
                  onChange={handleMemberTiersChange}
                  size="5"
                  disabled={templateEditMode}
                >
                  {memberTierOptions.map(tier => (
                    <option key={tier} value={tier}>{tier}</option>
                  ))}
                </select>
                <div className="form-helper-text">
                  Hold Ctrl/Cmd to select multiple tiers
                </div>
              </div>

              <div className="form-group">
                <label>Points Criteria</label>
                <select
                  value={formData.targetingCriteria.pointsType}
                  onChange={(e) => handleTargetingCriteriaChange('pointsType', e.target.value)}
                  disabled={templateEditMode}
                >
                  <option value="not_selected">Not Selected</option>
                  <option value="greater_than">Greater Than the Following Number of Points</option>
                  <option value="less_than">Less Than the Following Number of Points</option>
                </select>
              </div>

              <div className="form-group">
                <label>Number of Points</label>
                <input
                  type="number"
                  value={formData.targetingCriteria.pointsAmount}
                  onChange={(e) => handleTargetingCriteriaChange('pointsAmount', Number(e.target.value))}
                  min="0"
                  placeholder="0"
                  disabled={templateEditMode}
                />
              </div>
            </div>
          )}

          {formData.targetingType === 'customer_appointment' && (
            <div className="targeting-criteria">
              <div className="form-group">
                <label>Upcoming Request within</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    type="number"
                    value={formData.targetingCriteria.appointmentTimeValue}
                    onChange={(e) => handleTargetingCriteriaChange('appointmentTimeValue', Number(e.target.value))}
                    min="0"
                    disabled={templateEditMode}
                    style={{ width: '80px' }}
                  />
                  <select
                    value={formData.targetingCriteria.appointmentTimeUnit}
                    onChange={(e) => handleTargetingCriteriaChange('appointmentTimeUnit', e.target.value)}
                    disabled={templateEditMode}
                  >
                    <option value="hours">Hours</option>
                    <option value="days">Days</option>
                    <option value="months">Months</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Service Requested</label>
                <select
                  value={formData.targetingCriteria.appointmentService}
                  onChange={(e) => handleTargetingCriteriaChange('appointmentService', e.target.value)}
                  disabled={templateEditMode || loadingServicesStaff}
                >
                  <option value="not_selected">Not Selected (Any Service)</option>
                  {salonServices.map(service => (
                    <option key={service.id} value={service.id}>{service.name}</option>
                  ))}
                </select>
                {loadingServicesStaff && <span className="form-helper-text">Loading services...</span>}
              </div>

              <div className="form-group">
                <label>Staff Requested</label>
                <select
                  value={formData.targetingCriteria.appointmentStaff}
                  onChange={(e) => handleTargetingCriteriaChange('appointmentStaff', e.target.value)}
                  disabled={templateEditMode || loadingServicesStaff}
                >
                  <option value="not_selected">Not Selected (Any Staff)</option>
                  {salonStaff.map(staff => (
                    <option key={staff.id} value={staff.id}>{staff.name}</option>
                  ))}
                </select>
                {loadingServicesStaff && <span className="form-helper-text">Loading staff...</span>}
              </div>

              <div className="form-group">
                <label>Appointment Status</label>
                <select
                  multiple
                  value={formData.targetingCriteria.appointmentStatus}
                  onChange={(e) => {
                    const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
                    handleTargetingCriteriaChange('appointmentStatus', selectedOptions);
                  }}
                  size="4"
                  disabled={templateEditMode}
                >
                  {appointmentStatusOptions.map(status => (
                    <option key={status} value={status}>{status.charAt(0).toUpperCase() + status.slice(1)}</option>
                  ))}
                </select>
                <div className="form-helper-text">
                  Hold Ctrl/Cmd to select multiple statuses. Leave empty for any status.
                </div>
              </div>
            </div>
          )}

          {formData.targetingType === 'specific_customer' && (
            <div className="targeting-criteria">
              <div className="form-group">
                <label>
                  Search and Select Customers
                  {pushErrors.specificCustomers && <span className="error-text"> - {pushErrors.specificCustomers}</span>}
                </label>
                <div className="customer-search-container">
                  <input
                    type="text"
                    value={customerSearchQuery}
                    onChange={handleCustomerSearchChange}
                    onFocus={() => setShowCustomerDropdown(true)}
                    placeholder="Search by customer name or email..."
                    className="customer-search-input"
                    disabled={templateEditMode}
                  />
                  
                  {showCustomerDropdown && (
                    <div className="customer-search-dropdown">
                      {searchingCustomers && (
                        <div className="customer-search-loading">Searching...</div>
                      )}
                      
                      {!searchingCustomers && customerSearchResults.length === 0 && customerSearchQuery.length >= 2 && (
                        <div className="customer-search-no-results">No customers found</div>
                      )}
                      
                      {!searchingCustomers && customerSearchResults.length > 0 && (
                        <>
                          {customerSearchResults.map(customer => (
                            <div 
                              key={customer.id} 
                              className="customer-search-result"
                              onClick={() => handleCustomerSelect(customer)}
                            >
                              <div className="customer-result-name">{customer.displayName}</div>
                              <div className="customer-result-email">{customer.email}</div>
                              {customer.phoneNumber && (
                                <div className="customer-result-phone">{customer.phoneNumber}</div>
                              )}
                            </div>
                          ))}
                        </>
                      )}
                      
                      {customerSearchQuery.length < 2 && (
                        <div className="customer-search-hint">Type at least 2 characters to search</div>
                      )}
                    </div>
                  )}
                </div>
                
                {selectedCustomers.length > 0 && (
                  <div className="selected-customers">
                    <h5>Selected Customers ({selectedCustomers.length})</h5>
                    <div className="selected-customers-list">
                      {selectedCustomers.map(customer => (
                        <div key={customer.id} className="selected-customer-item">
                          <div className="selected-customer-info">
                            <div className="selected-customer-name">{customer.displayName}</div>
                            <div className="selected-customer-email">{customer.email}</div>
                          </div>
                          <button
                            type="button"
                            className="remove-customer-button"
                            onClick={() => handleCustomerRemove(customer.id)}
                            disabled={templateEditMode}
                          >
                            Remove
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* #Revised UI_v2 — customer notes targeting */}
          {formData.targetingType === 'customer_notes' && (
            <div className="targeting-criteria">
              <div className="form-group">
                <label>Search Terms</label>
                <input
                  type="text"
                  value={formData.targetingCriteria.notesSearchText || ''}
                  onChange={(e) => handleTargetingCriteriaChange('notesSearchText', e.target.value)}
                  placeholder="Enter words or phrase to search in customer notes..."
                  disabled={templateEditMode}
                />
                <div className="form-helper-text">Customers whose notes match the search terms will be targeted</div>
              </div>
              <div className="form-group">
                <label>Match Type</label>
                <select
                  value={formData.targetingCriteria.notesMatchType || 'contains_all'}
                  onChange={(e) => handleTargetingCriteriaChange('notesMatchType', e.target.value)}
                  disabled={templateEditMode}
                >
                  <option value="exact">Exact Match — notes contain this exact phrase</option>
                  <option value="contains_any">Contains Any Words — notes contain at least one of the words</option>
                  <option value="contains_all">Contains All Words — notes contain all words (any order)</option>
                </select>
              </div>
            </div>
          )}

          <div className="targeting-buttons">
            <button
              type="button"
              className="save-criteria-button"
              onClick={handleSaveTargetingCriteria}
              disabled={formData.targetingType === 'not_selected' || templateEditMode}
            >
              Save Targeting Criteria
            </button>
            <button
              type="button"
              className="preview-button"
              onClick={handlePreviewTargeting}
              disabled={previewLoading || !targetingCriteriaSaved || templateEditMode}
              style={{ opacity: targetingCriteriaSaved ? 1 : 0.5 }}
            >
              {previewLoading ? 'Loading...' : 'Preview Target Customers'}
            </button>
            {!templateEditMode && (
              <button
                type="button"
                className="add-template-button"
                onClick={handleCreateTemplate}
                disabled={formData.targetingType === 'not_selected'}
              >
                Add as Template
              </button>
            )}
            {!templateEditMode && (
              <button
                type="button"
                className="download-social-csv-button"
                onClick={handleDownloadSocialCSV}
                disabled={!targetingCriteriaSaved}
                style={{ opacity: targetingCriteriaSaved ? 1 : 0.5 }}
              >
                Download Target for Social Platforms (Meta, Instagram, TikTok)
              </button>
            )}
          </div>
        </div>

        {/* Final Customers Targeted Section */}
        <FinalCustomersTargeted
          salonId={salonId}
          campaignType="push"
          formData={formData}
          targetingCriteriaSaved={targetingCriteriaSaved}
          disabled={templateEditMode}
        />


        {templateEditMode ? (
          <div className="template-edit-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={handleSaveRevisedTemplate}
              disabled={saving !== ''}
            >
              {saving === 'template' ? 'Saving...' : 'Make Revisions to Template'}
            </button>
            <button
              type="button"
              className="cancel-button"
              onClick={() => {
                setTemplateEditMode(false);
                setTemplateToEdit(null);
                resetPushForm();
              }}
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="campaign-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={resetPushForm}
            >
              Cancel
            </button>

            <button
              type="button"
              className="test-blast-button"
              onClick={handleSendTestPushBlast}
              disabled={saving !== ''}
            >
              {saving === 'test' ? 'Sending...' : 'Send Test Blast'}
            </button>

            <button
              type="button"
              className="actual-blast-button"
              onClick={handleSendActualPushBlast}
              disabled={saving !== ''}
            >
              {saving === 'actual' ? 'Scheduling...' : 'Send Actual Blast'}
            </button>
          </div>
        )}
      </div>
      </>
      )}
    </div>
  );
}

export default PushCampaignsEditor;